// app/forum/ForumAds.js
'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import NextImage from 'next/image';
import ViewportAnimatedImage from '../../components/visual-runtime/ViewportAnimatedImage';
import useRenderManagedScope from '../../components/visual-runtime/useRenderManagedScope';
import { useI18n } from '../../components/i18n';
import { useRouter } from 'next/navigation';
import {
  AD_DISCOVERY_PROMPT_VARIANTS,
  AD_DISCOVERY_VISIBLE_MS,
  buildAdDiscoveryVariantOrder,
  getAdDiscoveryThresholdMs,
} from '../../lib/ads/adDiscoveryPrompt';
import {
  EXTERNAL_VIDEO_STATE_EVENT,
  emitExternalVideoState,
} from './features/media/utils/externalVideoBridge';

/* ======================= CAMPAIGN META ======================= */
const AD_LABEL_FONT_SIZE_PX = 20;

// ===== FIXED AD SLOT HEIGHT (px) =====
// Контент внутри вписывается, но высота/ширина слота не растут.
const AD_SLOT_HEIGHT_PX = {
  mobile: 520,   // < 640px
  tablet: 620,   // 640..1023px
  desktop: 650,  // >= 1024px
};
const AD_LENS_UI = Object.freeze({
  sizePx: 50,
  rightPx: 10,
  bottomPx: 10,

  strokeColor: '#6fe7ff',
  handleColor: '#b77dff',
  outlineColor: 'rgba(255, 215, 120, .96)',
  glowColor: 'rgba(111, 231, 255, .42)',
  glowColor2: 'rgba(183, 125, 255, .22)',

  opacity: 0.98,
  outlineWidth: 1,
  ringWidth: 1.8,
  handleWidth: 2.2,
  orbitWidth: 1,

  floatDurationMs: 2100,
  sweepDurationMs: 1650,
  pulseDurationMs: 1350,
  sparkleDurationMs: 980,
});
const CAMPAIGN_ID = 'forum_ads';
const FALLBACK_CAMPAIGN_SEED = 'forum_ads_seed';


/**
 * ВАЖНО: только статические обращения к NEXT_PUBLIC_*
 */
/* eslint-disable no-undef */
const ENV_AD = {
  EVERY: process.env.NEXT_PUBLIC_FORUM_AD_EVERY,
  ROTATE_MIN: process.env.NEXT_PUBLIC_FORUM_AD_ROTATE_MIN,
  DEBUG: process.env.NEXT_PUBLIC_FORUM_AD_DEBUG,
  LINKS: process.env.NEXT_PUBLIC_FORUM_AD_LINKS,
};
/* eslint-enable no-undef */

let cachedClientConf = null;
const mediaKindDetectCache = new Map();
const mediaKindDetectInflight = new Map();
const MEDIA_KIND_CACHE_OK_MS = 10 * 60 * 1000;
const MEDIA_KIND_CACHE_FAIL_MS = 45 * 1000;
const ADS_SLOT_HISTORY_LIMIT = 512;
export const FORUM_AD_IMAGE_DWELL_MS = 3 * 60 * 1000;

function setBoundedSlotHistory(map, key, value, limit = ADS_SLOT_HISTORY_LIMIT) {
  if (!(map instanceof Map)) return;
  if (map.has(key)) map.delete(key);
  map.set(key, value);
  while (map.size > Math.max(1, Number(limit || ADS_SLOT_HISTORY_LIMIT))) {
    const firstKey = map.keys().next().value;
    if (firstKey === undefined) break;
    map.delete(firstKey);
  }
}

const imageProbeCache = new Map();
const imageProbeInflight = new Map();
const IMAGE_PROBE_CACHE_OK_MS = 12 * 60 * 1000;
const IMAGE_PROBE_CACHE_FAIL_MS = 75 * 1000;
const adMediaResolveCache = new Map();
const adMediaResolveInflight = new Map();
const AD_MEDIA_RESOLVE_CACHE_OK_MS = 12 * 60 * 1000;
const AD_MEDIA_RESOLVE_CACHE_FAIL_MS = 10 * 60 * 1000;

// Visual default only. Runtime owners decide when to switch to auto/play.
const AD_NATIVE_VIDEO_PRELOAD_IDLE = 'metadata';

// ======================= ADS LINKS FROM REDIS =======================

let adsLinksMerged = false;
let adsLinksMergePromise = null;
let adsLinksLastAttemptTs = 0;
const ADS_LINKS_RETRY_MS = 5000;
export const FORUM_AD_LINKS_UPDATED_EVENT = 'forum:ads-links-updated';

function emitForumAdLinksUpdated(conf, detail = {}) {
  if (!isBrowser()) return;
  try {
    window.dispatchEvent(
      new CustomEvent(FORUM_AD_LINKS_UPDATED_EVENT, {
        detail: {
          source: 'forum-ads',
          links_len: Array.isArray(conf?.LINKS) ? conf.LINKS.length : 0,
          ...detail,
        },
      })
    );
  } catch {}
}

function pushForumAdLinkToken(out, clickRaw, mediaRaw, posterRaw = '', moderationStatusRaw = '', campaignIdRaw = '') {
  const click = String(clickRaw || '').trim();
  const media = String(mediaRaw || '').trim();
  const poster = String(posterRaw || '').trim();
  const moderationStatus = String(moderationStatusRaw || '').trim().toLowerCase();
  const campaignId = String(campaignIdRaw || '').trim();
  if (!click) return;
  if (!media) { out.push(click); return; }
  out.push(
    moderationStatus || campaignId
      ? `${click}|${media}|${poster}|${moderationStatus}|${campaignId}`
      : `${click}|${media}${poster ? `|${poster}` : ''}`
  );
}

function collectForumAdLinkTokens(payload, out = []) {
  if (!payload) return out;

  if (typeof payload === 'string') {
    if (payload.trim()) out.push(payload.trim());
    return out;
  }

  if (Array.isArray(payload)) {
    payload.forEach((item) => collectForumAdLinkTokens(item, out));
    return out;
  }

  if (typeof payload !== 'object') return out;

  const linksString =
    payload.linksString ??
    payload.links_string ??
    payload.forumLinksString ??
    payload.forum_links_string ??
    payload.linksRaw ??
    payload.links_raw ??
    null;

  if (typeof linksString === 'string' && linksString.trim()) {
    out.push(linksString.trim());
  }

  const directCollections = [
    payload.links,
    payload.forumLinks,
    payload.forum_links,
    payload.items,
    payload.campaigns,
    payload.ads,
    payload.activeAds,
    payload.active_ads,
    payload.activeCampaigns,
    payload.active_campaigns,
    payload.creatives,
  ];

  directCollections.forEach((value) => {
    if (value) collectForumAdLinkTokens(value, out);
  });

  if (payload.data && payload.data !== payload) {
    collectForumAdLinkTokens(payload.data, out);
  }

  if (payload.result && payload.result !== payload) {
    collectForumAdLinkTokens(payload.result, out);
  }

  if (payload.campaign && payload.campaign !== payload) {
    collectForumAdLinkTokens(payload.campaign, out);
  }

  if (payload.creative && payload.creative !== payload) {
    collectForumAdLinkTokens(payload.creative, out);
  }

  const clickUrl =
    payload.clickUrl ??
    payload.click_url ??
    payload.targetUrl ??
    payload.target_url ??
    payload.destinationUrl ??
    payload.destination_url ??
    payload.href ??
    payload.link ??
    payload.url ??
    null;

  const mediaUrl =
    payload.mediaUrl ??
    payload.media_url ??
    payload.imageUrl ??
    payload.image_url ??
    payload.videoUrl ??
    payload.video_url ??
    payload.assetUrl ??
    payload.asset_url ??
    payload.creativeUrl ??
    payload.creative_url ??
    payload.previewUrl ??
    payload.preview_url ??
    payload.media ??
    payload.image ??
    payload.video ??
    null;

  const posterUrl = payload.posterUrl ?? payload.poster_url ?? payload.poster ?? null;
  const moderationStatus = payload.moderationStatus ?? payload.moderation_status ?? null;
  const campaignId = payload.campaignId ?? payload.campaign_id ?? payload.id ?? null;
  pushForumAdLinkToken(out, clickUrl, mediaUrl, posterUrl, moderationStatus, campaignId);
  return out;
}

function readForumAdLinksStringFromPayload(payload) {
  const tokens = collectForumAdLinkTokens(payload, []);
  return tokens.map((item) => String(item || '').trim()).filter(Boolean).join('\n');
}

async function fetchForumAdLinksPayload() {
  const linksRes = await fetch('/api/ads?action=links', {
    method: 'GET',
    cache: 'no-store',
  }).catch(() => null);

  const linksPayload = linksRes?.ok
    ? await linksRes.json().catch(() => null)
    : null;

  const linksString = readForumAdLinksStringFromPayload(linksPayload);
  if (linksString) {
    return { linksString, source: 'links', payload: linksPayload };
  }

  const serveRes = await fetch('/api/ads?action=serve', {
    method: 'GET',
    cache: 'no-store',
  }).catch(() => null);

  const servePayload = serveRes?.ok
    ? await serveRes.json().catch(() => null)
    : null;

  const serveLinksString = readForumAdLinksStringFromPayload(servePayload);
  if (serveLinksString) {
    return { linksString: serveLinksString, source: 'serve', payload: servePayload };
  }

  return { linksString: '', source: 'empty', payload: linksPayload || servePayload };
}

async function mergeLinksFromRedisOnce() {
  if (!isBrowser()) return cachedClientConf;
  if (adsLinksMerged) return cachedClientConf;
  if (adsLinksMergePromise) return adsLinksMergePromise;

  const now = Date.now();
  if (adsLinksLastAttemptTs && now - adsLinksLastAttemptTs < ADS_LINKS_RETRY_MS) {
    return cachedClientConf;
  }
  adsLinksLastAttemptTs = now;

  adsLinksMergePromise = (async () => {
    try {
      // Read the main links endpoint first, then fallback to serve payload.
      // Keep parseLinks / MEDIA_BY_CLICK / picker as the single source of truth.
      const fetched = await fetchForumAdLinksPayload();
      if (!fetched.linksString) return cachedClientConf;

      const extraParsed = parseLinks(fetched.linksString);
      if (!extraParsed.links || !extraParsed.links.length) return cachedClientConf;

      const base = getForumAdConf(); // берём текущий кэш
      const baseLinks = Array.isArray(base.LINKS) ? base.LINKS : [];

      // Если ENV пустые — baseLinks = [], значит используем только базу.
      // Если ENV есть — объединяем ENV + база.
      const mergedLinks = baseLinks.length
        ? [...baseLinks, ...extraParsed.links]
        : extraParsed.links;

      // MEDIA_BY_CLICK теперь: clickUrl -> массив медиа
      const mergedMedia = { ...(base.MEDIA_BY_CLICK || {}) };
      const extraMedia = extraParsed.mediaByClick || {};
      const mergedPosters = { ...(base.POSTER_BY_MEDIA || {}) };
      Object.assign(mergedPosters, extraParsed.posterByMedia || {});
      const mergedModeration = { ...(base.MODERATION_BY_MEDIA || {}) };
      Object.assign(mergedModeration, extraParsed.moderationByMedia || {});
      const mergedCampaignIds = { ...(base.CAMPAIGN_BY_MEDIA || {}) };
      Object.assign(mergedCampaignIds, extraParsed.campaignByMedia || {});

      for (const [clickUrl, list] of Object.entries(extraMedia)) {
        if (!Array.isArray(list) || !list.length) continue;
        const prev = mergedMedia[clickUrl];
        if (!prev) {
          mergedMedia[clickUrl] = [...list];
        } else if (Array.isArray(prev)) {
          mergedMedia[clickUrl] = [...prev, ...list];
        } else {
          mergedMedia[clickUrl] = [prev, ...list];
        }
      }

      // БОЛЬШЕ НЕ ДЕДУПИМ ссылки — оставляем все вхождения
      const cleanedLinks = mergedLinks.filter(Boolean);

      base.LINKS = cleanedLinks;
      base.MEDIA_BY_CLICK = mergedMedia;
      base.POSTER_BY_MEDIA = mergedPosters;
      base.MODERATION_BY_MEDIA = mergedModeration;
      base.CAMPAIGN_BY_MEDIA = mergedCampaignIds;
      cachedClientConf = base;
      adsLinksMerged = true;

      debugLog(base, 'merge_links_from_redis', {
        env_len: baseLinks.length,
        extra_len: extraParsed.links.length,
        final_len: cleanedLinks.length,
      });

      emitForumAdLinksUpdated(base, {
        source: fetched.source || 'redis',
        env_len: baseLinks.length,
        extra_len: extraParsed.links.length,
        final_len: cleanedLinks.length,
      });

      return base;
    } catch {
      return cachedClientConf;
    } finally {
      adsLinksMergePromise = null;
    }
  })();

  return adsLinksMergePromise;
}

/* ======================= UTILS ======================= */

function isBrowser() {
  return typeof window !== 'undefined';
}

function getWin() {
  return isBrowser() ? window : null;
}

function debugLog(conf, ...args) {
  // ads debug disabled: no console output in browser
  return;
}


function hash32(str) {
  str = String(str || '');
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function stableShuffle(arr, seed) {
  const res = arr.slice();
  let s = seed || 1;
  for (let i = res.length - 1; i > 0; i--) {
    s = (s * 1664525 + 1013904223) >>> 0;
    const j = s % (i + 1);
    const t = res[i];
    res[i] = res[j];
    res[j] = t;
  }
  return res;
}

/* ======================= CONFIG ======================= */
/**
 * Приоритет:
 * 1) ?FORUM_AD_*
 * 2) localStorage.FORUM_AD_*
 * 3) window.FORUM_CONF.FORUM_AD_*
 * 4) NEXT_PUBLIC_FORUM_AD_* (ENV_AD)
 */

function readRaw(key) {
  const queryKey = `FORUM_AD_${key}`;

  const pick = (v) => {
    if (v == null) return null;
    const s = String(v).trim();
    return s === '' ? null : s;
  };

  // 1) query
  if (isBrowser()) {
    try {
      const sp = new URLSearchParams(window.location.search || '');
      if (sp.has(queryKey)) {
        const v = pick(sp.get(queryKey));
        if (v !== null) return v;
      }
    } catch {}
  }

  // 2) localStorage
  if (isBrowser()) {
    try {
      const v = pick(window.localStorage?.getItem(queryKey));
      if (v !== null) return v;
    } catch {}
  }

  // 3) window.FORUM_CONF / __FORUM_CONF__
  if (isBrowser()) {
    try {
      const src = window.FORUM_CONF || window.__FORUM_CONF__;
      if (src && Object.prototype.hasOwnProperty.call(src, queryKey)) {
        const v = pick(src[queryKey]);
        if (v !== null) return v;
      }
    } catch {}
  }

  // 4) ENV
  const envVal = pick(ENV_AD[key]);
  if (envVal !== null) return envVal;

  return null;
}

function parseNumber(v, def) {
  if (v == null) return def;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : def;
}

function normalizeUrl(raw) {
  if (!raw) return null;
  let s = String(raw).trim();
  if (!s) return null;

  if (!/^https?:\/\//i.test(s)) s = 'https://' + s;

  let u;
  try {
    u = new URL(s);
  } catch {
    return null;
  }

  if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;

  u.hostname = u.hostname.toLowerCase();

  if (u.pathname !== '/' && u.pathname.endsWith('/')) {
    u.pathname = u.pathname.slice(0, -1);
  }

  // сортированный query для стабильного hash
  if (u.search) {
    const entries = Array.from(u.searchParams.entries()).sort((a, b) =>
      a[0] > b[0] ? 1 : a[0] < b[0] ? -1 : 0
    );
    u.search = '';
    for (const [k, val] of entries) u.searchParams.append(k, val);
  }

  return u.toString();
}

/**
 * LINKS: CSV / ; / пробел / \n, поддержка # комментариев
 *
 * Формат одной записи:
 *   https://site.com
 *   https://click.url|https://media.url
 *
 * ▸ если одна часть → и кликаем, и показываем её
 * ▸ если через | → слева clickUrl, справа mediaUrl
 *
 * ВАЖНО: больше НЕТ дедупа — каждое вхождение остаётся.
 * Дополнительно:
 *   MEDIA_BY_CLICK[clickUrl] = [media1, media2, ...]
 */
function parseLinks(raw) {
  if (!raw) return { links: [], mediaByClick: {}, posterByMedia: {}, moderationByMedia: {}, campaignByMedia: {} };

  const tokens = String(raw)
    .split(/[\s,;]+/)
    .map((s) => s.replace(/#.*/, '').trim())
    .filter(Boolean);

  const links = [];
  const mediaByClick = {};
  const posterByMedia = {};
  const moderationByMedia = {};
  const campaignByMedia = {};

  for (const t of tokens) {
    const parts = t.split('|');
    const clickRaw = (parts[0] || '').trim();
    const mediaRaw = (parts[1] || '').trim();
    const posterRaw = (parts[2] || '').trim();
    const moderationRaw = (parts[3] || '').trim().toLowerCase();
    const campaignRaw = (parts[4] || '').trim();

    const clickNorm = normalizeUrl(clickRaw);
    if (!clickNorm) continue;

    const mediaNorm = normalizeUrl(mediaRaw || clickNorm) || clickNorm;
    const posterNorm = posterRaw ? normalizeUrl(posterRaw) : '';
    if (posterNorm && mediaRaw) posterByMedia[mediaNorm] = posterNorm;
    if (mediaRaw && moderationRaw) moderationByMedia[mediaNorm] = moderationRaw;
    if (mediaRaw && campaignRaw) campaignByMedia[mediaNorm] = campaignRaw;

    // добавляем каждое вхождение в общий список
    links.push(clickNorm);

    // собираем массив медиа для каждого клик-URL
    if (!mediaByClick[clickNorm]) {
      mediaByClick[clickNorm] = [mediaNorm];
    } else if (Array.isArray(mediaByClick[clickNorm])) {
      mediaByClick[clickNorm].push(mediaNorm);
    } else {
      mediaByClick[clickNorm] = [mediaByClick[clickNorm], mediaNorm];
    }
  }

  return { links, mediaByClick, posterByMedia, moderationByMedia, campaignByMedia };
}

export function getForumAdConf() {
  // ENV-ссылки и Redis/DB-ссылки — независимые источники.
  // Даже если cache уже создан из пустого NEXT_PUBLIC_FORUM_AD_LINKS,
  // Redis merge всё равно должен иметь шанс наполнить тот же cache.
  if (isBrowser()) {
    mergeLinksFromRedisOnce().catch(() => {});
  }

  if (isBrowser() && cachedClientConf) return cachedClientConf;

  const EVERY = parseNumber(readRaw('EVERY'), 0);
  const ROTATE_MIN = parseNumber(readRaw('ROTATE_MIN'), 1);

  const parsed = parseLinks(readRaw('LINKS') || '');
  const LINKS = parsed.links || [];
  const MEDIA_BY_CLICK = parsed.mediaByClick || {};
  const POSTER_BY_MEDIA = parsed.posterByMedia || {};
  const MODERATION_BY_MEDIA = parsed.moderationByMedia || {};
  const CAMPAIGN_BY_MEDIA = parsed.campaignByMedia || {};

  const DEBUG = String(readRaw('DEBUG') || '').trim() === '1';

  const conf = {
    EVERY,
    ROTATE_MIN,
    LINKS,
    MEDIA_BY_CLICK,
    POSTER_BY_MEDIA,
    MODERATION_BY_MEDIA,
    CAMPAIGN_BY_MEDIA,
    DEBUG,
    seed: hash32(`${CAMPAIGN_ID}:${FALLBACK_CAMPAIGN_SEED}:${LINKS.length}`),
  };

  if (isBrowser()) cachedClientConf = conf;

  debugLog(conf, 'config_built', {
    EVERY,
    ROTATE_MIN,
    LINKS_LEN: LINKS.length,
  });

  return conf;
}

/* ======================= INTERLEAVE ======================= */

export function interleaveAds(items, EVERY, opts = {}) {
  const { isSkippable, getId } = opts;
  const out = [];

  if (!Array.isArray(items) || !items.length) return [];

  const resolveItemKey = (item, index) => {
    const raw =
      (typeof getId === 'function' && getId(item)) ||
      item?.id ||
      item?.postId ||
      item?._id ||
      item?.uuid ||
      item?.key ||
      item?.topicId ||
      `idx${index}`;
    const stable = String(raw || `idx${index}`).trim();
    return stable || `idx${index}`;
  };

  if (!EVERY || EVERY <= 0) {
    return items.map((item, i) => {
      const itemKey = resolveItemKey(item, i);
      return { type: 'item', item, key: `item:${itemKey}` };
    });
  }

  let contentCount = 0;
  let adIndex = 0;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const itemKey = resolveItemKey(item, i);
    out.push({ type: 'item', item, key: `item:${itemKey}` });

    const skip =
      typeof isSkippable === 'function' ? !!isSkippable(item) : false;

    if (!skip) {
      contentCount++;
      if (contentCount % EVERY === 0) {
        const rawId =
          (typeof getId === 'function' && getId(item)) ||
          item?.id ||
          item?.postId ||
          item?.topicId ||
          `idx${i}`;
        const nearId = String(rawId || `idx${i}`);
        const key = `ad:${nearId}:${adIndex++}`;
        out.push({ type: 'ad', key, nearId });
      }
    }
  }

  const conf = getForumAdConf();
  debugLog(conf, 'interleave', {
    EVERY,
    in: items.length,
    out: out.length,
    ads: out.filter((s) => s.type === 'ad').length,
  });

  return out;
}

/* ======================= COORDINATOR ======================= */

class AdsCoordinatorImpl {
  constructor() {
    this.frames = new Map(); // frameId -> Set(url)
    this.history = []; // последние показы (глобально)
    this.lastBySeedKey = new Map(); // clientId|slotKey -> url
    this.rotationBySeedKey = new Map(); // clientId|slotKey -> { signature, seen:Set(url), cycle }
    this.flowRotationByClient = new Map(); // clientId -> global visible-flow shuffle bag
    this.preferredBySeedKey = new Map(); // clientId|slotKey -> prewarmed next click URL
    this.preferredMediaByKey = new Map(); // clickHref|slotKind|nearId -> prewarmed media href
    this.forumClientId = 'guest'; // forum picker + ForumAdSlot lifecycle must share one identity namespace
    this.lastFrameId = 0;
    this.lastFrameTs = 0;
  }

  _ensureFrame(frameId) {
    if (!this.frames.has(frameId)) {
      this.frames.set(frameId, new Set());
    }
    return this.frames.get(frameId);
  }

  getFrameId(nowMs) {
    const now = Number.isFinite(nowMs) ? nowMs : Date.now();

    if (!this.lastFrameId) {
      this.lastFrameId = 1;
      this.lastFrameTs = now;
    } else if (now - this.lastFrameTs > 250) {
      this.lastFrameId++;
      this.lastFrameTs = now;

      if (this.lastFrameId > 40) {
        const dropTo = this.lastFrameId - 20;
        for (const id of this.frames.keys()) {
          if (id < dropTo) this.frames.delete(id);
        }
      }
    }

    this._ensureFrame(this.lastFrameId);
    return this.lastFrameId;
  }

  reserve(url, frameId) {
    if (!url || frameId == null) return true;
    const bucket = this._ensureFrame(frameId);
    if (bucket.has(url)) return false; // не рисуем один и тот же урл в нескольких слотах одного кадра
    bucket.add(url);
    return true;
  }

  bump(url) {
    if (!url) return;
    this.history.push(url);
    if (this.history.length > 200) {
      this.history = this.history.slice(-200);
    }
  }

  getLastGlobal() {
    if (!this.history.length) return null;
    return this.history[this.history.length - 1];
  }

  allowed(url, linksLen) {
    if (!url) return false;
    const last = this.getLastGlobal();
    if (last && last === url && linksLen > 1) {
      // не даём тот же урл подряд глобально, если есть альтернатива
      return false;
    }
    return true;
  }

  setForumClientId(clientId) {
    const next = String(clientId || 'guest').trim() || 'guest';
    this.forumClientId = next;
    return next;
  }

  getForumClientId() {
    return String(this.forumClientId || 'guest').trim() || 'guest';
  }

  setLastForSeed(seedKey, url) {
    if (!seedKey || !url) return;
    setBoundedSlotHistory(this.lastBySeedKey, String(seedKey), url);
  }

  getLastForSeed(seedKey) {
    if (!seedKey) return null;
    return this.lastBySeedKey.get(String(seedKey)) || null;
  }

  getRotationState(seedKey, links) {
    const key = String(seedKey || 'slot');
    const identities = Array.from(new Set((Array.isArray(links) ? links : []).filter(Boolean)));
    const signature = `${identities.length}:${hash32(identities.join('\n'))}`;
    let state = this.rotationBySeedKey.get(key);

    if (!state || state.signature !== signature) {
      state = { signature, seen: new Set(), cycle: 0 };
      setBoundedSlotHistory(this.rotationBySeedKey, key, state);
    }

    // A cycle ends only after every distinct click URL has been exposed by this slot.
    // The immediately previous URL stays protected by resolveCurrentAdUrl().
    if (identities.length > 0 && state.seen.size >= identities.length) {
      state = { signature, seen: new Set(), cycle: Number(state.cycle || 0) + 1 };
      setBoundedSlotHistory(this.rotationBySeedKey, key, state);
    }

    return state;
  }

  hasSeenInRotation(seedKey, url, links) {
    if (!url) return false;
    const state = this.getRotationState(seedKey, links);
    return !!state?.seen?.has(url);
  }

  commitRotation(seedKey, url, links) {
    if (!url) return;
    const state = this.getRotationState(seedKey, links);
    state.seen.add(url);
    setBoundedSlotHistory(this.rotationBySeedKey, String(seedKey || 'slot'), state);
    if (this.preferredBySeedKey.get(String(seedKey || 'slot')) === url) {
      this.preferredBySeedKey.delete(String(seedKey || 'slot'));
    }
  }

  getFlowRotationState(clientId, links) {
    const key = String(clientId || 'guest');
    const identities = Array.from(new Set((Array.isArray(links) ? links : []).filter(Boolean)));
    const signature = `${identities.length}:${hash32(identities.join('\n'))}`;
    let state = this.flowRotationByClient.get(key);

    if (!state || state.signature !== signature) {
      state = { signature, seen: new Set(), cycle: 0 };
      setBoundedSlotHistory(this.flowRotationByClient, key, state);
    }

    if (identities.length > 0 && state.seen.size >= identities.length) {
      state = { signature, seen: new Set(), cycle: Number(state.cycle || 0) + 1 };
      setBoundedSlotHistory(this.flowRotationByClient, key, state);
    }

    return state;
  }

  hasSeenInFlow(clientId, url, links) {
    if (!url) return false;
    const state = this.getFlowRotationState(clientId, links);
    return !!state?.seen?.has(url);
  }

  commitFlowRotation(clientId, url, links) {
    if (!url) return;
    const key = String(clientId || 'guest');
    const state = this.getFlowRotationState(key, links);
    state.seen.add(url);
    setBoundedSlotHistory(this.flowRotationByClient, key, state);
  }

  setPreferredForSeed(seedKey, url) {
    const key = String(seedKey || 'slot');
    if (!url) {
      this.preferredBySeedKey.delete(key);
      return;
    }
    setBoundedSlotHistory(this.preferredBySeedKey, key, String(url));
  }

  getPreferredForSeed(seedKey) {
    return this.preferredBySeedKey.get(String(seedKey || 'slot')) || null;
  }

  setPreferredMedia(mediaKey, href) {
    const key = String(mediaKey || 'media');
    if (!href) {
      this.preferredMediaByKey.delete(key);
      return;
    }
    setBoundedSlotHistory(this.preferredMediaByKey, key, String(href));
  }

  takePreferredMedia(mediaKey, list) {
    const key = String(mediaKey || 'media');
    const href = this.preferredMediaByKey.get(key) || null;
    if (!href) return null;
    this.preferredMediaByKey.delete(key);
    return (Array.isArray(list) ? list : []).includes(href) ? href : null;
  }
}

export const AdsCoordinator = new AdsCoordinatorImpl();

/* ======================= URL PICKER ======================= */
/**
 * Детально:
 * - ROTATE_MIN остаётся временным cadence-сигналом для legacy/global runtime;
 * - сид завязан на (campaign, seed, clientId, slotKey, timeBucket);
 * - каждый слот имеет bounded shuffle-bag памяти: пока не показаны все distinct URL,
 *   уже показанный URL в этом слоте не выбирается снова;
 * - после исчерпания пула начинается новый цикл, но немедленный repeat всё равно запрещён;
 * - глобальный anti-repeat и same-frame reservation сохраняются.
 */

export function resolveCurrentAdUrl(
  rawConf,
  clientId,
  nowMs,
  slotKey,
  coordinator = AdsCoordinator
) {
  const baseConf = rawConf || getForumAdConf();
  const conf = { ...baseConf };

  let links = Array.isArray(conf.LINKS) ? conf.LINKS.filter(Boolean) : [];

  if (!links.length) {
    debugLog(conf, 'slot_no_links_no_fallback', { slotKey });
    return null;
  }

  const now = Number.isFinite(nowMs) ? nowMs : Date.now();
  const cid = String(clientId || 'guest');
  const key = String(slotKey || 'slot');
  const rotationKey = `${cid}|${key}`;

  const distinctLinks = Array.from(new Set(links.filter(Boolean)));
  const hasAlternative = distinctLinks.length > 1;
  const slotRotation = coordinator?.getRotationState?.(rotationKey, distinctLinks);
  const flowRotation = coordinator?.getFlowRotationState?.(cid, distinctLinks);

  const rotateMin = Number(conf.ROTATE_MIN || 1);
  const periodMs = Math.max(1, rotateMin) * 60_000;
  const timeBucket = Math.floor(now / periodMs);

  // The cycle salts make a freshly exhausted bag reshuffle instead of replaying the
  // previous order. Time still participates for compatibility with the legacy cadence.
  const seed = hash32(
    [
      CAMPAIGN_ID,
      conf.seed || 1,
      cid,
      key,
      timeBucket,
      Number(flowRotation?.cycle || 0),
      Number(slotRotation?.cycle || 0),
      distinctLinks.length,
    ].join('|')
  );

  const perm = stableShuffle(distinctLinks, seed);
  if (!perm.length) {
    debugLog(conf, 'slot_no_perm', { slotKey });
    return null;
  }

  const frameId = coordinator?.getFrameId(now);
  const lastForSeed = coordinator?.getLastForSeed(rotationKey);
  const lastGlobal = coordinator?.getLastGlobal();
  let chosen = null;
  const preferred = coordinator?.getPreferredForSeed?.(rotationKey) || null;

  const immediateAllowed = (candidate) =>
    !!candidate &&
    (!lastForSeed || candidate !== lastForSeed || !hasAlternative) &&
    (!lastGlobal || candidate !== lastGlobal || !hasAlternative);

  const frameAllowed = (candidate) =>
    !coordinator || frameId == null || coordinator.reserve(candidate, frameId);

  const strictAllowed = (candidate) =>
    immediateAllowed(candidate) &&
    !coordinator?.hasSeenInFlow?.(cid, candidate, distinctLinks) &&
    !coordinator?.hasSeenInRotation?.(rotationKey, candidate, distinctLinks);

  if (preferred && perm.includes(preferred) && strictAllowed(preferred) && frameAllowed(preferred)) {
    chosen = preferred;
  }

  // Pass 1: strongest contract. Across the user's visible ad flow and inside this
  // stable slot, do not reuse a click URL until the corresponding bag is exhausted.
  for (let i = 0; !chosen && i < perm.length; i++) {
    const candidate = perm[i];
    if (!strictAllowed(candidate)) continue;
    if (coordinator && !coordinator.allowed(candidate, distinctLinks.length)) continue;
    if (!frameAllowed(candidate)) continue;
    chosen = candidate;
  }

  // Pass 2: if independent slot/global bags conflict, protect the global stream first.
  // This is what the user actually experiences while scrolling through different slots.
  for (let i = 0; !chosen && i < perm.length; i++) {
    const candidate = perm[i];
    if (!immediateAllowed(candidate)) continue;
    if (coordinator?.hasSeenInFlow?.(cid, candidate, distinctLinks)) continue;
    chosen = candidate;
  }

  // Pass 3: preserve the slot-local bag when the global bag cannot supply a candidate.
  for (let i = 0; !chosen && i < perm.length; i++) {
    const candidate = perm[i];
    if (!immediateAllowed(candidate)) continue;
    if (coordinator?.hasSeenInRotation?.(rotationKey, candidate, distinctLinks)) continue;
    chosen = candidate;
  }

  // Final fallback exists only for single-item inventory or a mathematically
  // unavoidable collision between independent bags. Immediate repeats remain blocked
  // whenever an alternative exists.
  if (!chosen) {
    chosen = perm.find((candidate) => immediateAllowed(candidate)) || perm[0] || null;
    if (!chosen) {
      debugLog(conf, 'slot_no_pick', { slotKey, linksLen: perm.length });
      return null;
    }
  }

  if (coordinator) {
    coordinator.commitRotation?.(rotationKey, chosen, distinctLinks);
    coordinator.commitFlowRotation?.(cid, chosen, distinctLinks);
    coordinator.bump(chosen);
    coordinator.setLastForSeed(rotationKey, chosen);
  }

  debugLog(conf, 'slot_pick', {
    slotKey: key,
    rotationKey,
    url: chosen,
    distinctLinks: distinctLinks.length,
    slotCycle: Number(slotRotation?.cycle || 0),
    flowCycle: Number(flowRotation?.cycle || 0),
  });

  return chosen;
}

/* ======================= PRECONNECT ======================= */

export function useAdPreconnect() {
  useEffect(() => {
    // Direct-media-only mode: no external page-preview preconnects.
  }, []);
}

/* ======================= EVENTS & MEDIA ======================= */

function emitAdEvent(type, payload, conf) {
  const detail = {
    type,
    ts: Date.now(),
    campaignId: CAMPAIGN_ID,
    ...payload,
  };
  debugLog(conf, type, detail);

  const w = getWin();
  if (w && typeof w.dispatchEvent === 'function') {
    try {
      w.dispatchEvent(new CustomEvent('ads:event', { detail }));
    } catch {}
  }

  // Серверная аналитика для пользовательских кампаний
  if (type === 'ad_impression' || type === 'ad_click') {
    try {
      const body = {
        action: 'event', // работаем через единый /api/ads
        type, // 'ad_impression' | 'ad_click'
        url_hash: detail.url_hash,
        slot_kind: detail.slot_kind,
        near_id: detail.near_id,
      };
      fetch('/api/ads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* no-op */
    }
  }
}

function tryLoadImage(src, timeoutMs = 3000) {
  const key = String(src || '').trim();
  if (!key || !isBrowser() || !('Image' in window)) return Promise.resolve(false);

  const now = Date.now();
  const cached = imageProbeCache.get(key);
  if (cached && Number(cached.until || 0) > now) return Promise.resolve(!!cached.ok);
  if (cached) imageProbeCache.delete(key);

  const inflight = imageProbeInflight.get(key);
  if (inflight) return inflight;

  const task = new Promise((resolve) => {
    const img = new window.Image();
    let done = false;
    const finish = (ok) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      imageProbeCache.set(key, {
        ok: !!ok,
        until: Date.now() + (ok ? IMAGE_PROBE_CACHE_OK_MS : IMAGE_PROBE_CACHE_FAIL_MS),
      });
      if (imageProbeCache.size > 360) {
        let drop = imageProbeCache.size - 360;
        for (const oldKey of imageProbeCache.keys()) {
          imageProbeCache.delete(oldKey);
          drop -= 1;
          if (drop <= 0) break;
        }
      }
      resolve(!!ok);
    };
    img.onload = () => finish(true);
    img.onerror = () => finish(false);
    try { img.referrerPolicy = 'no-referrer'; } catch {}
    const timer = setTimeout(() => finish(false), timeoutMs);
    img.src = key;
  });

  imageProbeInflight.set(key, task);
  return task.finally(() => {
    imageProbeInflight.delete(key);
  });
}

function shouldProbeMediaKind(rawUrl) {
  const s = String(rawUrl || '').trim();
  if (!s) return false;
  if (/^blob:/i.test(s)) return true;
  if (/\.(webm|mp4|mov|m4v|mkv|jpe?g|png|webp|gif|avif)(?:$|[?#])/i.test(s)) return true;
  if (/[?&]filename=.*\.(webm|mp4|mov|m4v|mkv|jpe?g|png|webp|gif|avif)(?:$|[?#])/i.test(s)) return true;
  if (/vercel[-]?storage|vercel[-]?blob|\/uploads\/video|\/forum\/video|\/api\/forum\/uploadVideo/i.test(s)) return true;
  try {
    const u = new URL(s, window.location.href);
    if (u.origin === window.location.origin) {
      // Не пробуем HEAD/GET на произвольных slug-страницах внутри нашего origin
      // (например news/article permalink), иначе это создаёт лишние красные ошибки в HAR.
      const path = String(u.pathname || '').toLowerCase();
      if (/\.(webm|mp4|mov|m4v|mkv|jpe?g|png|webp|gif|avif)$/.test(path)) return true;
      if (
        path.startsWith('/forum/video') ||
        path.startsWith('/uploads/video') ||
        path.startsWith('/api/forum/uploadvideo') ||
        path.startsWith('/api/forum/wa-preview')
      ) return true;
      return false;
    }
  } catch {}
  return false;
}

// Определяем тип медиа по Content-Type через HEAD
async function detectMediaKind(url, timeoutMs = 3000, opts = {}) {
  if (!url || !isBrowser() || typeof fetch === 'undefined') return null;

  const key = String(url).trim();
  if (!key) return null;
  if (!shouldProbeMediaKind(key)) return null;

  const now = Date.now();
  const cached = mediaKindDetectCache.get(key);
  if (cached && Number(cached.until || 0) > now) {
    return cached.kind || null;
  }
  if (cached) mediaKindDetectCache.delete(key);

  const inflight = mediaKindDetectInflight.get(key);
  if (inflight) return inflight;

  const classify = (ct) => {
    const s = String(ct || '').toLowerCase();
    if (!s) return null;
    if (s.startsWith('video/')) return 'video';
    if (s.startsWith('image/')) return 'image';
    return null;
  };
  const persist = (kind, ok = false) => {
    mediaKindDetectCache.set(key, {
      kind: kind || null,
      until: Date.now() + (ok ? MEDIA_KIND_CACHE_OK_MS : MEDIA_KIND_CACHE_FAIL_MS),
    });
    if (mediaKindDetectCache.size > 320) {
      let drop = mediaKindDetectCache.size - 320;
      for (const oldKey of mediaKindDetectCache.keys()) {
        mediaKindDetectCache.delete(oldKey);
        drop -= 1;
        if (drop <= 0) break;
      }
    }
    return kind || null;
  };

  const task = (async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
const headResp = await fetch(key, {
  method: 'HEAD',
  signal: controller.signal,
  cache: 'default',
  referrerPolicy: 'no-referrer',
}).catch(() => null);
      if (headResp) {
        const byHeadCt = classify(headResp.headers.get('content-type'));
        if (byHeadCt) return persist(byHeadCt, true);
      }

// В рекламе по умолчанию НЕ делаем GET Range bytes=0-1.
// Такой probe сам даёт лишний 206 для native video.
if (opts?.allowRangeFallback === true) {
  const getResp = await fetch(key, {
    method: 'GET',
    headers: { Range: 'bytes=0-1' },
    signal: controller.signal,
    cache: 'default',
    referrerPolicy: 'no-referrer',
  }).catch(() => null);
  if (getResp) {
    const byGetCt = classify(getResp.headers.get('content-type'));
    if (byGetCt) return persist(byGetCt, true);
  }
}

      return persist(null, false);
    } catch {
      return persist(null, false);
    } finally {
      clearTimeout(timer);
    }
  })();

  mediaKindDetectInflight.set(key, task);
  try {
    return await task;
  } finally {
    mediaKindDetectInflight.delete(key);
  }
}

function readCachedAdResolvedMedia(key) {
  const nextKey = String(key || '').trim();
  if (!nextKey) return null;
  const row = adMediaResolveCache.get(nextKey);
  if (!row) return null;
  if (Number(row.until || 0) <= Date.now()) {
    adMediaResolveCache.delete(nextKey);
    return null;
  }
  return row.media || null;
}

function cacheAdResolvedMedia(key, media, ok = true) {
  const nextKey = String(key || '').trim();
  if (!nextKey || !media) return;
  adMediaResolveCache.set(nextKey, {
    media,
    until: Date.now() + (ok ? AD_MEDIA_RESOLVE_CACHE_OK_MS : AD_MEDIA_RESOLVE_CACHE_FAIL_MS),
  });
  if (adMediaResolveCache.size > 420) {
    let drop = adMediaResolveCache.size - 420;
    for (const oldKey of adMediaResolveCache.keys()) {
      adMediaResolveCache.delete(oldKey);
      drop -= 1;
      if (drop <= 0) break;
    }
  }
}

function isLikelyVideoUrl(raw) {
  if (!raw) return false;
  const s = String(raw).trim();
  if (!s) return false;

  if (/^blob:/i.test(s)) return true;

  // нормальные видео по расширению
  if (/\.(webm|mp4|mov|m4v|mkv)(?:$|[?#])/i.test(s)) return true;

  // filename=...mp4 в query
  if (/[?&]filename=.*\.(webm|mp4|mov|m4v|mkv)(?:$|[?#])/i.test(s)) return true;

  // только явные video-эндпоинты
  if (
    /vercel[-]?storage|vercel[-]?blob|\/uploads\/video|\/forum\/video|\/api\/forum\/uploadVideo/i.test(
      s
    )
  ) {
    return true;
  }

  return false;
}

const YT_RE =
  /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{6,})/i;

const TIKTOK_RE =
  /^(?:https?:\/\/)?(?:www\.)?tiktok\.com\/(?:@[\w.\-]+\/video\/(\d+)|t\/[A-Za-z0-9]+)(?:[?#].*)?$/i;

/* ====== вспомогательное хранилище для анти-повтора медиы по слоту ====== */

const lastMediaIndexByKey = new Map();

/* ====== controlled visual helpers ====== */

function normalizeAdMuted(value) {
  // Only explicit false means sound is on. Safe default: muted.
  return value === false ? false : true;
}

function mergeRefs(...refs) {
  return (node) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === 'function') {
        try { ref(node); } catch {}
      } else {
        try { ref.current = node; } catch {}
      }
    });
  };
}

function toSafeClickUrl(rawUrl) {
  try {
    const u = new URL(rawUrl);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    return u;
  } catch {
    return null;
  }
}

function buildYouTubeEmbedSrc(videoId) {
  const id = String(videoId || '').trim();
  if (!id) return '';

  const url = new URL(`https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}`);
  url.searchParams.set('enablejsapi', '1');
  url.searchParams.set('controls', '0');
  url.searchParams.set('rel', '0');
  url.searchParams.set('fs', '0');
  url.searchParams.set('modestbranding', '1');
  url.searchParams.set('playsinline', '1');
  url.searchParams.set('loop', '0');
  url.searchParams.set('autoplay', '0');
  url.searchParams.set('mute', '1');

  try {
    if (isBrowser() && window.location?.origin) {
      url.searchParams.set('origin', window.location.origin);
    }
  } catch {}

  return url.toString();
}

function buildTikTokEmbedSrc(videoId) {
  const id = String(videoId || '').trim();
  if (!id) return '';

  const url = new URL(`https://www.tiktok.com/embed/v2/${encodeURIComponent(id)}`);
  url.searchParams.set('autoplay', '0');
  return url.toString();
}

function readMediaListForClick(conf, clickHref) {
  const entry = conf?.MEDIA_BY_CLICK?.[clickHref] ?? null;

  if (Array.isArray(entry) && entry.length) return entry.filter(Boolean);
  if (typeof entry === 'string' && entry.trim()) return [entry.trim()];

  return clickHref ? [clickHref] : [];
}

function normalizeAdMediaIdentity(raw) {
  try {
    const text = String(raw || '').trim();
    if (!text) return '';
    const url = new URL(text, typeof window !== 'undefined' ? window.location.href : 'https://ql7.local/');
    url.hash = '';
    const normalizedPath = String(url.pathname || '').replace(/\/+$/, '').toLowerCase();
    return `${url.hostname.toLowerCase()}${normalizedPath}${url.search ? url.search.toLowerCase() : ''}`;
  } catch {
    return String(raw || '').trim().toLowerCase();
  }
}

const adMediaRecentHistory = [];
const adMediaLastIndexByKey = new Map();

function readLiveAdMediaIdentities() {
  const live = new Set();
  try {
    if (typeof document === 'undefined') return live;
    document.querySelectorAll('video[data-ad-media="1"], video[data-site-ad-media="video"], [data-ad-media-src], [data-site-ad-src]').forEach((node) => {
      try {
        const src = String(
          node.currentSrc ||
          node.getAttribute?.('src') ||
          node.getAttribute?.('data-src') ||
          node.getAttribute?.('data-ad-media-src') ||
          node.getAttribute?.('data-site-ad-src') ||
          ''
        ).trim();
        const id = normalizeAdMediaIdentity(src);
        if (id) live.add(id);
      } catch {}
    });
  } catch {}
  return live;
}

function rememberAdMediaIdentity(identity, href, mediaKey) {
  try {
    const id = String(identity || '').trim();
    if (!id) return;
    adMediaRecentHistory.push({ id, href: String(href || ''), mediaKey: String(mediaKey || ''), ts: Date.now() });
    while (adMediaRecentHistory.length > 30) adMediaRecentHistory.shift();
    if (typeof window !== 'undefined') {
      window.__ql7ForumAdRotationState = () => ({
        recent: adMediaRecentHistory.slice(-20),
        live: Array.from(readLiveAdMediaIdentities()),
      });
    }
  } catch {}
}

function pickAdMediaHref(list, mediaKey, prevIndex = -1) {
  try {
    const items = (Array.isArray(list) ? list : []).filter(Boolean);
    if (!items.length) return { href: '', index: -1, identity: '' };
    if (items.length === 1) {
      const href = String(items[0] || '');
      return { href, index: 0, identity: normalizeAdMediaIdentity(href) };
    }
    const live = readLiveAdMediaIdentities();
    const recentIds = new Set(adMediaRecentHistory.slice(-10).map((row) => row.id).filter(Boolean));
    const scored = items.map((href, index) => {
      const identity = normalizeAdMediaIdentity(href);
      const livePenalty = identity && live.has(identity) ? 10000 : 0;
      const recentPenalty = identity && recentIds.has(identity) ? 1400 : 0;
      const repeatPenalty = index === prevIndex ? 900 : 0;
      const jitter = Math.floor(Math.random() * 41);
      return { href: String(href || ''), index, identity, score: livePenalty + recentPenalty + repeatPenalty + jitter };
    }).sort((a, b) => a.score - b.score);
    return scored[0] || { href: String(items[0] || ''), index: 0, identity: normalizeAdMediaIdentity(items[0]) };
  } catch {
    const href = String((Array.isArray(list) && list[0]) || '');
    return { href, index: 0, identity: normalizeAdMediaIdentity(href) };
  }
}


const adSuccessorPrewarmCache = new Map();

function scheduleAdAssetPrewarm(mediaHref) {
  if (!isBrowser()) return;
  const href = String(mediaHref || '').trim();
  if (!href) return;

  const now = Date.now();
  const last = Number(adSuccessorPrewarmCache.get(href) || 0);
  if (last && now - last < 90_000) return;
  setBoundedSlotHistory(adSuccessorPrewarmCache, href, now, 120);

  const run = () => {
    if (isLikelyVideoUrl(href)) {
      try {
        const video = document.createElement('video');
        video.muted = true;
        video.preload = 'metadata';
        video.playsInline = true;
        video.src = href;
        video.load?.();
        window.setTimeout(() => {
          try { video.pause?.(); } catch {}
          try { video.removeAttribute('src'); video.load?.(); } catch {}
        }, 12_000);
      } catch {}
      return;
    }

    // Only direct image-like assets are warmed here. External provider embeds keep
    // their existing lifecycle and are not opened speculatively.
    if (/youtube\.com|youtu\.be|tiktok\.com/i.test(href)) return;
    try {
      const img = new Image();
      img.decoding = 'async';
      img.referrerPolicy = 'no-referrer';
      img.src = href;
    } catch {}
  };

  try {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(run, { timeout: 1200 });
    } else {
      window.setTimeout(run, 120);
    }
  } catch {
    run();
  }
}

export function prewarmNextAdCreative({
  conf: confProp,
  clientId = 'guest',
  slotKey = 'slot',
  slotKind = '',
  nearId = '',
  currentUrl = '',
  coordinator = AdsCoordinator,
} = {}) {
  const conf = confProp || getForumAdConf();
  const links = Array.isArray(conf?.LINKS) ? conf.LINKS.filter(Boolean) : [];
  if (!links.length) return null;

  const cid = String(clientId || 'guest');
  const key = String(slotKey || 'slot');
  const rotationKey = `${cid}|${key}`;
  const distinctLinks = Array.from(new Set(links));
  const current = String(currentUrl || '');
  const slotState = coordinator?.getRotationState?.(rotationKey, distinctLinks);
  const flowState = coordinator?.getFlowRotationState?.(cid, distinctLinks);

  const now = Date.now();
  const rotateMin = Number(conf.ROTATE_MIN || 1);
  const periodMs = Math.max(1, rotateMin) * 60_000;
  const timeBucket = Math.floor(now / periodMs);
  const seed = hash32(
    [
      CAMPAIGN_ID,
      conf.seed || 1,
      cid,
      key,
      timeBucket,
      Number(flowState?.cycle || 0),
      Number(slotState?.cycle || 0),
      links.length,
      'prewarm',
    ].join('|')
  );
  const perm = stableShuffle(distinctLinks, seed);

  let nextClick = perm.find((candidate) => {
    if (!candidate || candidate === current) return false;
    if (flowState?.seen?.has(candidate)) return false;
    if (slotState?.seen?.has(candidate)) return false;
    return true;
  });

  if (!nextClick) {
    nextClick = perm.find((candidate) => candidate && candidate !== current && !flowState?.seen?.has(candidate)) || null;
  }
  if (!nextClick) {
    nextClick = perm.find((candidate) => candidate && candidate !== current && !slotState?.seen?.has(candidate)) || null;
  }
  if (!nextClick) {
    nextClick = perm.find((candidate) => candidate && candidate !== current) || null;
  }
  if (!nextClick && distinctLinks.length === 1) {
    const onlyClick = distinctLinks[0];
    if (readMediaListForClick(conf, onlyClick).length > 1) nextClick = onlyClick;
  }
  if (!nextClick) return null;

  coordinator?.setPreferredForSeed?.(rotationKey, nextClick);

  const list = readMediaListForClick(conf, nextClick);
  const mediaKey = `${nextClick}::${slotKind || ''}::${nearId || ''}`;
  const prevIndex = adMediaLastIndexByKey.has(mediaKey)
    ? adMediaLastIndexByKey.get(mediaKey)
    : (lastMediaIndexByKey.has(mediaKey) ? lastMediaIndexByKey.get(mediaKey) : -1);
  const planned = pickAdMediaHref(list, mediaKey, prevIndex);
  const mediaHref = String(planned?.href || '');
  if (mediaHref) {
    coordinator?.setPreferredMedia?.(mediaKey, mediaHref);
    scheduleAdAssetPrewarm(mediaHref);
  }

  return { clickHref: nextClick, mediaHref };
}

function emitAdFallback(conf, clickHref, slotKind, cascadeStep) {
  emitAdEvent(
    'ad_fallback',
    { url: clickHref, cascade_step: cascadeStep, slot_kind: slotKind },
    conf
  );
}

export async function resolveAdMediaPayload(mediaHref, opts = {}) {
  const key = String(mediaHref || '').trim();
  if (!key) return { kind: 'skeleton', src: null };

  const conf = opts.conf || getForumAdConf();
  const knownPoster = String(conf?.POSTER_BY_MEDIA?.[key] || '').trim();
  const knownModerationStatus = String(conf?.MODERATION_BY_MEDIA?.[key] || '').trim().toLowerCase();
  const knownCampaignId = String(conf?.CAMPAIGN_BY_MEDIA?.[key] || '').trim();
  const withVideoModerationMeta = (media) => media?.kind === 'video'
    ? {
        ...media,
        ...(knownPoster ? { poster: knownPoster } : {}),
        ...(knownModerationStatus ? { moderationStatus: knownModerationStatus } : {}),
        ...(knownCampaignId ? { campaignId: knownCampaignId } : {}),
      }
    : media;
  const cached = readCachedAdResolvedMedia(key);
  if (cached) return withVideoModerationMeta(cached);

  const inflight = adMediaResolveInflight.get(key);
  if (inflight) return inflight;

  const task = (async () => {
    const clickHref = String(opts.clickHref || key);
    const slotKind = opts.slotKind;

    const publish = (media, okCache = true, cascadeStep = '') => {
      const enriched = withVideoModerationMeta(media);
      cacheAdResolvedMedia(key, enriched, okCache);
      if (cascadeStep) emitAdFallback(conf, clickHref, slotKind, cascadeStep);
      return enriched;
    };

    if (isLikelyVideoUrl(key)) {
      return publish({ kind: 'video', src: key, poster: String(conf?.POSTER_BY_MEDIA?.[key] || ''), step: 'env_video' }, true, 'env_video');
    }

    if (shouldProbeMediaKind(key)) {
      const detected = await detectMediaKind(key, 3000, {
        allowRangeFallback: false,
      }).catch(() => null);

      if (detected === 'video') {
        return publish({ kind: 'video', src: key, poster: String(conf?.POSTER_BY_MEDIA?.[key] || ''), step: 'head_video' }, true, 'head_video');
      }

      if (detected === 'image') {
        return publish({ kind: 'image', src: key, step: 'head_image' }, true, 'head_image');
      }
    }

    const ytMatch = key.match(YT_RE);
    if (ytMatch?.[1]) {
      return publish({ kind: 'youtube', src: ytMatch[1], step: 'env_youtube' }, true, 'env_youtube');
    }

    const ttMatch = key.match(TIKTOK_RE);
    if (ttMatch) {
      let videoId = ttMatch[1] || null;
      if (!videoId) {
        try {
          videoId = new URL(key).pathname.match(/\/video\/(\d+)/)?.[1] || null;
        } catch {}
      }
      if (videoId) {
        return publish({ kind: 'tiktok', src: videoId, step: 'env_tiktok' }, true, 'env_tiktok');
      }
    }

    if (/\.(jpe?g|png|webp|gif|avif)(?:$|[?#])/i.test(key)) {
      const ok = await tryLoadImage(key);
      if (ok) return publish({ kind: 'image', src: key, step: 'direct_image' }, true, 'direct_image');
    }

    return publish({ kind: 'placeholder', src: null, step: 'placeholder' }, false, 'placeholder');
  })();

  adMediaResolveInflight.set(key, task);
  try {
    return await task;
  } finally {
    adMediaResolveInflight.delete(key);
  }
}

export function useAdMediaPayload({
  url,
  slotKind,
  nearId,
  conf: confProp,
  freezeForMount = false,
} = {}) {
  const conf = useMemo(() => confProp || getForumAdConf(), [confProp]);
  const safeClick = useMemo(() => toSafeClickUrl(url), [url]);
  const clickHref = safeClick ? safeClick.toString() : '';
  const host = safeClick ? safeClick.hostname.replace(/^www\./i, '') : '';
  const mediaKey = useMemo(
    () => `${clickHref}::${slotKind || ''}::${nearId || ''}`,
    [clickHref, slotKind, nearId]
  );
  const frozenMediaHrefRef = useRef({ key: '', href: '' });
  const [mediaHref, setMediaHref] = useState(null);
  const [media, setMedia] = useState({ kind: 'skeleton', src: null });

  useEffect(() => {
    if (!clickHref) {
      if (freezeForMount) frozenMediaHrefRef.current = { key: mediaKey, href: '' };
      setMediaHref(null);
      setMedia({ kind: 'skeleton', src: null });
      return undefined;
    }

    if (freezeForMount) {
      const frozen = frozenMediaHrefRef.current;
      if (frozen.key === mediaKey && frozen.href) {
        setMediaHref(frozen.href);
        return undefined;
      }
      if (frozen.key !== mediaKey) {
        frozenMediaHrefRef.current = { key: mediaKey, href: '' };
      }
    }

    const list = readMediaListForClick(conf, clickHref);
    if (!list.length) {
      setMediaHref(null);
      setMedia({ kind: 'placeholder', src: null, step: 'placeholder' });
      return undefined;
    }

    let idx = adMediaLastIndexByKey.has(mediaKey)
      ? adMediaLastIndexByKey.get(mediaKey)
      : (lastMediaIndexByKey.has(mediaKey) ? lastMediaIndexByKey.get(mediaKey) : -1);

    const commitMediaHref = (nextHref, nextIdentity = '') => {
      if (freezeForMount) {
        frozenMediaHrefRef.current = { key: mediaKey, href: nextHref };
      }
      if (nextIdentity) rememberAdMediaIdentity(nextIdentity, nextHref, mediaKey);
      setMediaHref(nextHref);
    };

    const pickNext = () => {
      const preferredHref = AdsCoordinator?.takePreferredMedia?.(mediaKey, list) || '';
      const preferredIndex = preferredHref ? list.indexOf(preferredHref) : -1;
      const picked = preferredIndex >= 0
        ? {
            href: String(preferredHref),
            index: preferredIndex,
            identity: normalizeAdMediaIdentity(preferredHref),
          }
        : pickAdMediaHref(list, mediaKey, idx);
      idx = Number(picked.index || 0);
      setBoundedSlotHistory(adMediaLastIndexByKey, mediaKey, idx);
      setBoundedSlotHistory(lastMediaIndexByKey, mediaKey, idx);
      commitMediaHref(picked.href, picked.identity);
    };

    pickNext();
    if (freezeForMount) return undefined;

    const rotateMs = Math.max(
      10_000,
      Number(conf?.ROTATE_MIN || 1) * 60_000
    );
    const timer = setInterval(pickNext, rotateMs);

    return () => {
      clearInterval(timer);
    };
  }, [clickHref, conf, freezeForMount, mediaKey]);

  useEffect(() => {
    if (!clickHref || !mediaHref) {
      setMedia({ kind: 'skeleton', src: null });
      return undefined;
    }

    let cancelled = false;
    const key = String(mediaHref || '').trim();
    const cached = readCachedAdResolvedMedia(key);
    if (cached) {
      const moderationStatus = String(conf?.MODERATION_BY_MEDIA?.[key] || '').trim().toLowerCase();
      const campaignId = String(conf?.CAMPAIGN_BY_MEDIA?.[key] || '').trim();
      setMedia(cached.kind === 'video' ? {
        ...cached,
        ...(moderationStatus ? { moderationStatus } : {}),
        ...(campaignId ? { campaignId } : {}),
      } : cached);
      return undefined;
    }

    if (isLikelyVideoUrl(key)) setMedia({ kind: 'video', src: key, step: 'env_video_idle' });
    else setMedia({ kind: 'skeleton', src: null });

    resolveAdMediaPayload(key, { conf, clickHref, slotKind })
      .then((next) => {
        if (!cancelled && next) setMedia(next);
      })
      .catch(() => {
        if (!cancelled) setMedia({ kind: 'placeholder', src: null, step: 'placeholder_error' });
      });

    return () => {
      cancelled = true;
    };
  }, [clickHref, conf, mediaHref, slotKind]);

  return { conf, safeClick, clickHref, host, mediaHref, media };
}

/* ======================= AdCard ======================= */
/**
 * Direct media only: native video → direct image → YouTube/TikTok → placeholder.
 * Бейдж "Реклама" из словаря: ключ forum_ad_label.
 * Внешние page-preview ветки отключены: OpenGraph/Microlink/screenshot/favicon.
 */

export function AdCard({
  url,
  slotKind,
  nearId,
  layout = 'fixed',
  muted = true,
  onSoundToggle,
  onMediaSurfaceActivate,
  onOpenAdsPage,
  rootRef,
  videoRef,
  iframeRef,
  rootAttrs,
  mediaSlotAttrs,
  videoAttrs,
  youtubeAttrs,
  tiktokAttrs,
  deferNativeSrc = false,
  deferExternalSrc = false,
  freezeMediaForMount = false,
  tiktokActive = true,
  // The feed slot arbitrates native ad DOM residency. Does not change the
  // campaign/creative picker or the video_ended/viewport_exit rotation.
  nativeAdVideoMountAllowed = true,
  onMediaChange,
  onExternalEnded,
}) {
  const conf = getForumAdConf();
  useAdPreconnect(conf);
  const i18n = useI18n();
  const t = i18n?.t;
  const router = useRouter();
  const isFluid = layout === 'fluid';
  const localRootRef = useRef(null);
  useRenderManagedScope(localRootRef, { kind: 'ad', marginProfile: 'near100', initialNear: true });
  const localVideoRef = useRef(null);
  const localIframeRef = useRef(null);
  const { safeClick, clickHref, host, mediaHref, media } = useAdMediaPayload({
    url,
    slotKind,
    nearId,
    conf,
    freezeForMount: freezeMediaForMount,
  });
  const [discoveryVisible, setDiscoveryVisible] = useState(false);
  const [discoveryVariantIndex, setDiscoveryVariantIndex] = useState(0);
  const [liveModerationStatus, setLiveModerationStatus] = useState(
    String(media?.moderationStatus || '').trim().toLowerCase()
  );
  const [externalPosterVisible, setExternalPosterVisible] = useState(true);
  const externalEndedRef = useRef('');

  useEffect(() => {
    setLiveModerationStatus(String(media?.moderationStatus || '').trim().toLowerCase());
  }, [media?.campaignId, media?.moderationStatus, media?.src]);

  useEffect(() => {
    // A new external item always starts poster-first.
    externalEndedRef.current = '';
    setExternalPosterVisible(true);
  }, [media?.kind, media?.src]);

  useEffect(() => {
    if (!isBrowser()) return undefined;
    if (media?.kind !== 'youtube' && media?.kind !== 'tiktok') return undefined;
    const onExternalState = (event) => {
      const frame = localIframeRef.current;
      if (!frame || event?.detail?.frame !== frame) return;
      if (typeof event?.detail?.paused === 'boolean') {
        setExternalPosterVisible(!!event.detail.paused);
      }
    };
    window.addEventListener(EXTERNAL_VIDEO_STATE_EVENT, onExternalState);
    return () => window.removeEventListener(EXTERNAL_VIDEO_STATE_EVENT, onExternalState);
  }, [media?.kind, media?.src]);

  useEffect(() => {
    if (!isBrowser() || !onExternalEnded) return undefined;
    if (media?.kind !== 'youtube' && media?.kind !== 'tiktok') return undefined;
    const frame = localIframeRef.current;
    if (!frame) return undefined;

    const notifyEnded = () => {
      const identity = `${String(media?.kind || '')}:${String(media?.src || '')}`;
      if (!identity || externalEndedRef.current === identity) return;
      externalEndedRef.current = identity;
      try { onExternalEnded({ kind: media.kind, src: media.src, iframe: frame }); } catch {}
    };

    const onMessage = (event) => {
      try {
        if (event?.source !== frame.contentWindow) return;
        let data = event?.data;
        if (typeof data === 'string') {
          try { data = JSON.parse(data); } catch { data = { event: data }; }
        }
        if (!data || typeof data !== 'object') return;

        const eventName = String(data.event || data.type || data.method || '').trim().toLowerCase();
        const info = data.info;
        const nestedState =
          data?.value?.state ??
          data?.value?.playerState ??
          data?.state ??
          data?.playerState ??
          (info && typeof info === 'object' ? (info.playerState ?? info.state) : undefined);

        const infoObject = info && typeof info === 'object' ? info : null;
        const valueObject = data?.value && typeof data.value === 'object' ? data.value : null;
        const currentTime = Number(
          infoObject?.currentTime ?? valueObject?.currentTime ?? data?.currentTime,
        );
        const duration = Number(
          infoObject?.duration ?? valueObject?.duration ?? data?.duration,
        );
        const providerMuted =
          typeof infoObject?.muted === 'boolean'
            ? infoObject.muted
            : (typeof valueObject?.muted === 'boolean' ? valueObject.muted : undefined);
        const stateNumber = Number(nestedState);
        emitExternalVideoState(frame, {
          ...(Number.isFinite(currentTime) && currentTime >= 0 ? { currentTime } : {}),
          ...(Number.isFinite(duration) && duration >= 0 ? { duration } : {}),
          ...(typeof providerMuted === 'boolean' ? { muted: providerMuted } : {}),
          ...(Number.isFinite(stateNumber) && [0, 1, 2].includes(stateNumber)
            ? { paused: stateNumber !== 1 }
            : {}),
        });

        const youtubeEnded =
          media.kind === 'youtube' &&
          ((eventName === 'onstatechange' && Number(info) === 0) || Number(nestedState) === 0);
        const tiktokEnded =
          media.kind === 'tiktok' &&
          [eventName, String(nestedState || '').toLowerCase()].some((value) =>
            value === 'ended' || value === 'end' || value === 'videoended'
          );

        if (youtubeEnded || tiktokEnded) notifyEnded();
      } catch {}
    };

    window.addEventListener('message', onMessage);

    if (media.kind === 'youtube') {
      try {
        frame.contentWindow?.postMessage?.(JSON.stringify({ event: 'listening', id: `ql7-ad-${Date.now()}` }), '*');
        frame.contentWindow?.postMessage?.(JSON.stringify({ event: 'command', func: 'addEventListener', args: ['onStateChange'] }), '*');
      } catch {}
    }

    return () => window.removeEventListener('message', onMessage);
  }, [media?.kind, media?.src, onExternalEnded]);

  useEffect(() => {
    const campaignId = String(media?.campaignId || '').trim();
    const status = String(liveModerationStatus || media?.moderationStatus || '').trim().toLowerCase();
    if (!isBrowser() || media?.kind !== 'video' || !campaignId || status !== 'pending') return undefined;
    let cancelled = false;
    let timer = 0;
    const poll = async () => {
      try {
        const params = new URLSearchParams({ surface: 'ads', entityId: campaignId, mediaUrl: String(media?.src || '') });
        const response = await fetch(`/api/forum/moderate?${params.toString()}`, { cache: 'no-store' });
        const json = await response.json().catch(() => null);
        const nextStatus = String(json?.videoModeration?.status || '').trim().toLowerCase();
        if (!cancelled && ['approved', 'rejected', 'gone'].includes(nextStatus)) {
          setLiveModerationStatus(nextStatus);
          return;
        }
      } catch {}
      if (!cancelled) timer = window.setTimeout(poll, 2400);
    };
    void poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [liveModerationStatus, media?.campaignId, media?.kind, media?.moderationStatus, media?.src]);

  useEffect(() => {
    setDiscoveryVisible(false);
    setDiscoveryVariantIndex(0);
    if (!isBrowser() || media.kind !== 'video' || !media.src || !clickHref) return undefined;

    const video = localVideoRef.current;
    if (!video) return undefined;

    let interval = 0;
    let hideTimer = 0;
    let watchedMs = 0;
    let lastTickMs = 0;
    let promptNumber = 0;
    let variantCursor = 0;
    let variantOrder = buildAdDiscoveryVariantOrder();

    const clearTicker = () => {
      if (interval) window.clearInterval(interval);
      interval = 0;
      lastTickMs = 0;
    };

    const hidePrompt = () => {
      setDiscoveryVisible(false);
      if (hideTimer) window.clearTimeout(hideTimer);
      hideTimer = 0;
    };

    const showPrompt = () => {
      if (!variantOrder.length || variantCursor >= variantOrder.length) {
        variantOrder = buildAdDiscoveryVariantOrder();
        variantCursor = 0;
      }
      const nextVariant = variantOrder[variantCursor] ?? 0;
      variantCursor += 1;
      setDiscoveryVariantIndex(nextVariant);
      setDiscoveryVisible(true);
      if (hideTimer) window.clearTimeout(hideTimer);
      hideTimer = window.setTimeout(hidePrompt, AD_DISCOVERY_VISIBLE_MS);
    };

    const tick = () => {
      if (document.visibilityState === 'hidden' || video.paused || video.ended) {
        lastTickMs = 0;
        return;
      }
      const now = performance.now();
      if (!lastTickMs) {
        lastTickMs = now;
        return;
      }
      watchedMs += Math.min(1000, Math.max(0, now - lastTickMs));
      lastTickMs = now;
      const threshold = getAdDiscoveryThresholdMs(promptNumber);
      if (watchedMs >= threshold) {
        promptNumber += 1;
        showPrompt();
      }
    };

    const startTicker = () => {
      if (interval) return;
      lastTickMs = performance.now();
      interval = window.setInterval(tick, 250);
    };
    const stopTicker = () => {
      clearTicker();
    };

    video.addEventListener('playing', startTicker);
    video.addEventListener('play', startTicker);
    video.addEventListener('pause', stopTicker);
    video.addEventListener('ended', stopTicker);
    if (!video.paused && !video.ended) startTicker();

    return () => {
      clearTicker();
      if (hideTimer) window.clearTimeout(hideTimer);
      video.removeEventListener('playing', startTicker);
      video.removeEventListener('play', startTicker);
      video.removeEventListener('pause', stopTicker);
      video.removeEventListener('ended', stopTicker);
    };
  }, [clickHref, media.kind, media.src]);

  useEffect(() => {
    onMediaChange?.({ media, mediaHref, clickHref, slotKind, nearId });
  }, [clickHref, media, mediaHref, nearId, onMediaChange, slotKind]);

  const slotCssVars = {
    '--ad-slot-h-m': `${AD_SLOT_HEIGHT_PX.mobile}px`,
    '--ad-slot-h-t': `${AD_SLOT_HEIGHT_PX.tablet}px`,
    '--ad-slot-h-d': `${AD_SLOT_HEIGHT_PX.desktop}px`,

    '--forum-ad-lens-size': `${AD_LENS_UI.sizePx}px`,
    '--forum-ad-lens-right': `${AD_LENS_UI.rightPx}px`,
    '--forum-ad-lens-bottom': `${AD_LENS_UI.bottomPx}px`,

    '--forum-ad-lens-stroke': AD_LENS_UI.strokeColor,
    '--forum-ad-lens-handle': AD_LENS_UI.handleColor,
    '--forum-ad-lens-outline': AD_LENS_UI.outlineColor,
    '--forum-ad-lens-glow': AD_LENS_UI.glowColor,
    '--forum-ad-lens-glow-2': AD_LENS_UI.glowColor2,

    '--forum-ad-lens-opacity': String(AD_LENS_UI.opacity),
    '--forum-ad-lens-outline-w': `${AD_LENS_UI.outlineWidth}px`,
    '--forum-ad-lens-ring-w': `${AD_LENS_UI.ringWidth}px`,
    '--forum-ad-lens-handle-w': `${AD_LENS_UI.handleWidth}px`,
    '--forum-ad-lens-orbit-w': `${AD_LENS_UI.orbitWidth}px`,

    '--forum-ad-lens-float-dur': `${AD_LENS_UI.floatDurationMs}ms`,
    '--forum-ad-lens-sweep-dur': `${AD_LENS_UI.sweepDurationMs}ms`,
    '--forum-ad-lens-pulse-dur': `${AD_LENS_UI.pulseDurationMs}ms`,
    '--forum-ad-lens-sparkle-dur': `${AD_LENS_UI.sparkleDurationMs}ms`,
  };

  // Impression tracking
  useEffect(() => {
    const el = localRootRef.current;
    if (
      !el ||
      !isBrowser() ||
      typeof IntersectionObserver === 'undefined' ||
      !safeClick
    ) {
      return undefined;
    }

    let seen = false;
    let timer = null;

    const observer = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (!e || seen) return;

        if (e.intersectionRatio >= 0.5) {
          if (!timer) {
            timer = setTimeout(() => {
              if (seen) return;
              seen = true;
              emitAdEvent(
                'ad_impression',
                {
                  url: safeClick.toString(),
                  url_hash: hash32(safeClick.toString()),
                  slot_kind: slotKind,
                  near_id: nearId,
                },
                conf
              );
            }, 800);
          }
        } else if (timer) {
          clearTimeout(timer);
          timer = null;
        }
      },
      { threshold: [0.5] }
    );

    observer.observe(el);
    return () => {
      if (timer) clearTimeout(timer);
      observer.disconnect();
    };
  }, [safeClick, slotKind, nearId, conf]);

  useEffect(() => {
    const el = localRootRef.current;
    if (!el || !isBrowser() || media.kind !== 'video') return undefined;

    let releaseTimer = 0;
    const setCompositorActive = (active) => {
      if (releaseTimer) {
        window.clearTimeout(releaseTimer);
        releaseTimer = 0;
      }
      if (active) {
        el.dataset.forumNativeCompositor = '1';
        return;
      }

      // Chromium can briefly report an ad as outside the viewport while scrolling.
      // Keep the layer alive through that transition so the native video plane does not strobe.
      releaseTimer = window.setTimeout(() => {
        try { el.dataset.forumNativeCompositor = '0'; } catch {}
        releaseTimer = 0;
      }, 1800);
    };

    if (typeof IntersectionObserver === 'undefined') {
      setCompositorActive(true);
      return () => {
        if (releaseTimer) window.clearTimeout(releaseTimer);
        try { delete el.dataset.forumNativeCompositor; } catch {}
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setCompositorActive(!!entry?.isIntersecting);
      },
      {
        root: null,
        rootMargin: '240px 0px 240px 0px',
        threshold: [0, 0.01],
      },
    );

    observer.observe(el);
    return () => {
      if (releaseTimer) window.clearTimeout(releaseTimer);
      observer.disconnect();
      try { delete el.dataset.forumNativeCompositor; } catch {}
    };
  }, [media.kind]);

  if (!safeClick) return null;
  if (liveModerationStatus === 'rejected' || liveModerationStatus === 'gone') return null;

  const label =
    typeof t === 'function'
      ? t('forum_ad_label', 'Реклама')
      : 'Реклама';

  // Кнопка «Разместить» — переходим на страницу /ads, не кликая по рекламной ссылке
  const handleOpenAdsPage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { onOpenAdsPage?.(e); } catch {}
    try {
      router.push('/ads');
    } catch {
      if (typeof window !== 'undefined') {
        window.location.href = '/ads';
      }
    }
  };

  const handleDestinationClick = (event) => {
    event?.stopPropagation?.();
    setDiscoveryVisible(false);
    emitAdEvent(
      'ad_click',
      {
        url: clickHref,
        url_hash: hash32(clickHref),
        slot_kind: slotKind,
        near_id: nearId,
      },
      conf
    );
  };

  const isAdMuted = normalizeAdMuted(muted);
  const canActivateMedia =
    (media.kind === 'video' || media.kind === 'youtube' || media.kind === 'tiktok') &&
    typeof onMediaSurfaceActivate === 'function';

  const activateMediaSurface = (event) => {
    if (!canActivateMedia) return;
    const target = event?.target;
    const interactive = target?.closest?.(
      'a,button,[data-ad-destination-trigger="1"],[data-ad-sound-toggle="1"]',
    );
    if (interactive && interactive !== event?.currentTarget) return;
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (media.kind === 'youtube' || media.kind === 'tiktok') {
      // Match the original proven surface contract: a trusted manual Play gesture
      // dismisses the poster immediately. Do not gate the UI on provider postMessage state.
      setExternalPosterVisible(false);
    }
    onMediaSurfaceActivate?.(event, {
      media,
      mediaHref,
      clickHref,
      slotKind,
      nearId,
      video: localVideoRef.current,
      iframe: localIframeRef.current,
    });
  };

  const handleMediaSurfaceKeyDown = (event) => {
    if (!canActivateMedia || (event.key !== 'Enter' && event.key !== ' ')) return;
    activateMediaSurface(event);
  };

  const handleToggleSound = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onSoundToggle?.(e, {
      muted: isAdMuted,
      nextMuted: !isAdMuted,
      media,
      mediaHref,
      clickHref,
      slotKind,
      nearId,
    });
  };

  const showSoundButton =
    (media.kind === 'video' || media.kind === 'youtube') &&
    typeof onSoundToggle === 'function';

  const videoAttrBag = videoAttrs || {};
  const youtubeAttrBag = youtubeAttrs || {};
  const tiktokAttrBag = tiktokAttrs || {};
  const {
    className: videoClassName,
    ...restVideoAttrs
  } = videoAttrBag;
  const {
    className: youtubeClassName,
    ...restYoutubeAttrs
  } = youtubeAttrBag;
  const {
    className: tiktokClassName,
    ...restTiktokAttrs
  } = tiktokAttrBag;

  const youtubeSrc = media.kind === 'youtube' && media.src ? buildYouTubeEmbedSrc(media.src) : '';
  const tiktokSrc = media.kind === 'tiktok' && media.src ? buildTikTokEmbedSrc(media.src) : '';
  const externalPosterSrc = String(media?.poster || '').trim() || (
    media.kind === 'youtube' && media.src
      ? `https://i.ytimg.com/vi/${encodeURIComponent(String(media.src).trim())}/hqdefault.jpg`
      : ''
  );
  const discoveryVariant =
    AD_DISCOVERY_PROMPT_VARIANTS[discoveryVariantIndex] || AD_DISCOVERY_PROMPT_VARIANTS[0];
  const discoveryTitle = discoveryVariant
    ? t?.(discoveryVariant.titleKey, 'Discover what is behind this offer')
    : '';
  const discoveryBody = discoveryVariant
    ? t?.(discoveryVariant.bodyKey, 'Tap here to learn more about the product or service.')
    : '';
  const mediaActivationLabel = t?.(
    'forum_ad_media_activate_aria',
    'Play this advertisement with sound',
  );
  const destinationLabel = t?.(
    'forum_ad_discovery_open_aria',
    'Open product or service details',
  );

  return (
<div
  ref={mergeRefs(localRootRef, rootRef)}
  className="item forum-ad-card"
  dir="ltr"
  data-slot-kind={slotKind}
  data-ads="1"
  data-ql7-render-managed="1" data-ql7-visual-scope="ad"
  data-stable-shell="1"
  data-windowing-keepalive="media"
  data-forum-windowing-stable="1"
  data-forum-native-video-card={media.kind === 'video' ? '1' : undefined}
  style={slotCssVars}
  {...(rootAttrs || {})}
>
<style jsx>{`
  .forum-ad-card {
    direction: ltr !important;
    width: 100%;
    min-height: var(--ad-slot-h-m);
    height: var(--ad-slot-h-m);
    max-height: var(--ad-slot-h-m);
    overflow: hidden;
    contain: layout paint;
  }
  .forum-ad-card[data-forum-native-video-card="1"],
  .forum-ad-card[data-forum-native-video-card="1"]:hover {
    transform: none !important;
    will-change: auto;
  }
  @media (min-width: 640px) {
    .forum-ad-card {
      direction: ltr !important;
      min-height: var(--ad-slot-h-t);
      height: var(--ad-slot-h-t);
      max-height: var(--ad-slot-h-t);
    }
  }
  @media (min-width: 1024px) {
    .forum-ad-card {
      direction: ltr !important;
      min-height: var(--ad-slot-h-d);
      height: var(--ad-slot-h-d);
      max-height: var(--ad-slot-h-d);
    }
  }
  .forum-ad-link {
    display: block;
    height: 100%;
    min-height: 0;
  }
  .forum-ad-card-body {
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: .25rem;
  }
  .forum-ad-header {
    flex: 0 0 auto;
    min-height: 30px;
  }
  .forum-ad-media-slot {
    width: 100%;
    position: relative;         /* ключ: якорь для absolute медиа */
    overflow: hidden;
    border-radius: 0.5rem;
    background: var(--bg-soft, #000000);
  }
  .forum-ad-media-fill {
    overflow: hidden;
    isolation: isolate;
    background: #000;
  }
  /* Keep the advertising creative off the browser's native media hit target.
     The slot remains interactive and owns activation, while right-clicking the
     visual surface now resolves to the ordinary page context menu just like
     the canonical forum media surfaces. */
  .forum-ad-fit {
    pointer-events: none !important;
    user-select: none;
    -webkit-user-select: none;
    -webkit-user-drag: none;
  }
  .forum-ad-ambient-backdrop {
    position: absolute !important;
    inset: -2px !important;
    z-index: 0 !important;
    display: block !important;
    width: calc(100% + 4px) !important;
    height: calc(100% + 4px) !important;
    min-width: 0 !important;
    min-height: 0 !important;
    max-width: none !important;
    max-height: none !important;
    margin: 0 !important;
    object-fit: cover !important;
    object-position: center !important;
    transform: scale(1.06);
    filter: brightness(.3) saturate(.78) contrast(1.04);
    opacity: 1;
    pointer-events: none;
    user-select: none;
    -webkit-user-drag: none;
  }
  .forum-ad-external-play-button {
    appearance: none;
    -webkit-appearance: none;
    position: relative;
    z-index: 3;
    display: block;
    width: clamp(72px, 20%, 118px);
    max-width: 28vw;
    height: auto;
    margin: 0;
    padding: 0;
    border: 0;
    outline: 0;
    background: transparent;
    box-shadow: none;
    cursor: pointer;
    pointer-events: auto;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  .forum-ad-external-play-button > img {
    display: block;
    width: 100%;
    height: auto;
    object-fit: contain;
    transform: translateY(0) scale(1);
    transition: transform 145ms cubic-bezier(.2,.9,.25,1.18);
    will-change: transform;
    pointer-events: none;
    user-select: none;
    -webkit-user-drag: none;
  }
  .forum-ad-external-play-button:hover > img,
  .forum-ad-external-play-button:focus-visible > img {
    transform: translateY(-4px) scale(1.07);
  }
  .forum-ad-external-play-button:active > img {
    transform: translateY(1px) scale(.93);
    transition-duration: 70ms;
  }
  @media (prefers-reduced-motion: reduce) {
    .forum-ad-external-play-button > img { transition: none; }
  }
  .forum-video-moderation-badge{
    position:absolute; top:12px; left:12px; z-index:24;
    display:inline-flex; align-items:center; justify-content:center;
    min-height:28px; padding:5px 11px; border-radius:999px;
    border:1px solid rgba(255,170,58,.86);
    background:linear-gradient(135deg, rgba(69,31,4,.94), rgba(16,12,7,.92));
    color:#ffd39a; font-size:11px; font-weight:800; line-height:1; letter-spacing:.08em;
    box-shadow:0 0 14px rgba(255,126,22,.68),0 0 30px rgba(255,126,22,.30),0 7px 18px rgba(0,0,0,.42);
    pointer-events:none; user-select:none; animation:forumVideoModerationPulse 1.9s ease-in-out infinite;
  }
  @keyframes forumVideoModerationPulse{ 0%,100%{opacity:.92;filter:brightness(.96)} 50%{opacity:1;filter:brightness(1.16)} }
  @media (prefers-reduced-motion: reduce){ .forum-video-moderation-badge{animation:none;} }
  /* ===== FIXED (как в форуме сейчас) ===== */
  .forum-ad-media-slot[data-layout="fixed"] {
    height: var(--ad-slot-h-m);
  }
  @media (min-width: 640px) {
.forum-ad-media-slot[data-layout="fixed"] {
      height: var(--ad-slot-h-t);
    }
  }

  @media (min-width: 1024px) {
    .forum-ad-media-slot[data-layout="fixed"] {
      height: var(--ad-slot-h-d);
    }
  }

  /* ===== FLUID (для рендера по всему сайту) ===== */
  .forum-ad-card .forum-ad-media-slot[data-layout="fixed"] {
    flex: 1 1 auto;
    min-height: 0;
    height: auto;
  }

  .forum-ad-media-slot[data-layout="fluid"] {
    height: auto;
    overflow: visible;
  }

  /* fixed: слой медиа растягиваем на весь слот */
  .forum-ad-media-slot[data-layout="fixed"] .forum-ad-media-fill {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }

.forum-ad-media-slot[data-layout="fixed"] .forum-ad-fit {
    position: relative;
    z-index: 1;
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
    display: block;
    background: transparent;
  }
  .forum-ad-fit[data-ad-media-kind="video"] {
    opacity: 1;
    transform: none;
    -webkit-transform: none;
    backface-visibility: visible;
    -webkit-backface-visibility: visible;
    will-change: auto;
    filter: none;
  }
  .forum-ad-card[data-forum-native-compositor="1"] .forum-ad-fit[data-ad-media-kind="video"] {
    opacity: .9999;
    transform: translate3d(0, 0, 0);
    -webkit-transform: translate3d(0, 0, 0);
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
  }
  /* fluid: слой медиа не absolute — иначе высота не считается */
  .forum-ad-media-slot[data-layout="fluid"] .forum-ad-media-fill {
    position: relative;
    width: 100%;
    height: auto;
  }

  .forum-ad-media-slot[data-layout="fluid"] .forum-ad-fit {
    position: relative;
    z-index: 1;
    width: 100%;
    height: auto;
    object-fit: contain;
    display: block;
    background: transparent;
  }

  .forum-ad-lens {
    position: absolute;
    right: var(--forum-ad-lens-right, 10px);
    bottom: var(--forum-ad-lens-bottom, 10px);
    width: var(--forum-ad-lens-size, 40px);
    height: var(--forum-ad-lens-size, 40px);
    z-index: 9;
    pointer-events: auto;
    cursor: pointer;
    touch-action: manipulation;
    user-select: none;
    -webkit-user-select: none;
    opacity: var(--forum-ad-lens-opacity, .98);
    transform: translateZ(0);
    transform-origin: 50% 50%;
    filter:
      drop-shadow(0 0 8px var(--forum-ad-lens-glow, rgba(111, 231, 255, .42)))
      drop-shadow(0 0 16px var(--forum-ad-lens-glow-2, rgba(183, 125, 255, .22)));
    border-radius: 999px;
    outline: none;
    animation:
      forumAdLensFloat var(--forum-ad-lens-float-dur, 2100ms) ease-in-out infinite,
      forumAdLensAura 3200ms linear infinite;
  }
  .forum-ad-lens::before,
  .forum-ad-lens::after {
    content: '';
    position: absolute;
    inset: 7%;
    border-radius: 999px;
    pointer-events: none;
  }
  .forum-ad-lens::before {
    border: 1px solid rgba(111, 231, 255, .5);
    animation: forumAdLensRadar 1900ms ease-out infinite;
  }
  .forum-ad-lens::after {
    inset: 28%;
    background: radial-gradient(circle, rgba(255, 231, 133, .34), rgba(111, 231, 255, .08) 48%, transparent 70%);
    animation: forumAdLensCoreGlow 1200ms ease-in-out infinite;
  }
  .forum-ad-lens:focus-visible {
    box-shadow: 0 0 0 2px rgba(255, 228, 123, .94), 0 0 0 5px rgba(111, 231, 255, .3);
  }
  .forum-ad-audio-toggle{
    position:absolute;
    left: calc(
      var(--forum-ad-lens-right, 10px) +
      var(--forum-ad-lens-size, 10px) - 35px
    );
    bottom: var(--forum-ad-lens-bottom, 10px);
    width: 40px;
    height: 40px;
    padding: 0;
    border-radius: 999px;
    display:grid;
    place-items:center;
    z-index: 7;
    pointer-events:auto;
    touch-action: manipulation;
    user-select:none;
    -webkit-user-select:none;
    -webkit-tap-highlight-color: transparent;
    overflow: visible;

    border: 1px solid rgba(157, 221, 255, 0);
    color:#e6f4ff;
    background:
      radial-gradient(120% 120% at 30% 30%, rgba(255, 255, 255, 0), rgba(255,255,255,0) 60%),
      radial-gradient(100% 100% at 70% 70%, rgba(0, 200, 255, 0), rgba(0,200,255,0) 60%),
      linear-gradient(180deg, rgba(0, 35, 60, 0), rgba(0, 35, 60, 0));
    box-shadow:
      inset 0 0 18px rgba(0, 179, 255, 0),
      0 8px 20px rgba(0, 0, 0, 0);
    backdrop-filter: none;
    -webkit-backdrop-filter: none;

    transition:
      transform .18s ease,
      filter .18s ease,
      box-shadow .18s ease,
      border-color .18s ease;
  }

  .forum-ad-audio-toggle:hover{
    transform: translateY(-1px) scale(1.03);
    filter: brightness(1.06);
  }

  .forum-ad-audio-toggle:active{
    transform: scale(.98);
  }

  .forum-ad-audio-toggle:focus-visible{
    outline: none;
    box-shadow:
      inset 0 0 18px rgba(0,180,255,.16),
      0 0 0 2px rgba(255,228,123,.94),
      0 0 0 5px rgba(111,231,255,.3);
  }

  .forum-ad-audio-toggle__visual{
    position: relative;
    width: 34px;
    height: 34px;
    display: block;
    pointer-events: none;
    user-select: none;
    -webkit-user-select: none;
  }

  .forum-ad-audio-toggle__gif{
    width: 100%;
    height: 100%;
    display: block;
    object-fit: contain;
    border-radius: 8px;
    filter:
      drop-shadow(0 0 6px rgba(64,201,255,.34))
      drop-shadow(0 0 12px rgba(0,179,255,.18));
    user-select: none;
    -webkit-user-select: none;
    -webkit-user-drag: none;
  }

  .forum-ad-audio-toggle__slash{
    position: absolute;
    left: 50%;
    top: 50%;
    width: 30px;
    height: 3px;
    border-radius: 999px;
    background: linear-gradient(90deg, rgba(255,40,40,.95), rgba(255,110,110,.98));
    box-shadow:
      0 0 8px rgba(255,50,50,.6),
      0 0 16px rgba(255,0,0,.25);
    transform: translate(-50%, -50%) rotate(-38deg) scale(1);
    transform-origin: center;
    opacity: 1;
    transition: opacity .18s ease, transform .18s ease;
  }

  .forum-ad-audio-toggle.on{
    animation: forumAdAudioPulse 3s ease-out infinite;
  }

  .forum-ad-audio-toggle.on .forum-ad-audio-toggle__slash{
    opacity: 0;
    transform: translate(-50%, -50%) rotate(-38deg) scale(.85);
  }

  .forum-ad-audio-toggle.off .forum-ad-audio-toggle__gif{
    filter:
      grayscale(.1)
      brightness(.9)
      drop-shadow(0 0 2px rgba(255,80,80,.18));
  }

  @keyframes forumAdAudioPulse{
    0%{
      box-shadow:
        inset 0 0 18px rgba(0,180,255,.16),
        0 0 0 0 rgba(0,194,255,.35);
    }
    60%{
      box-shadow:
        inset 0 0 18px rgba(0,180,255,.16),
        0 0 0 12px rgba(0,194,255,0);
    }
    100%{
      box-shadow:
        inset 0 0 18px rgba(0,180,255,.16),
        0 0 0 0 rgba(0,194,255,0);
    }
  }

  @media (max-width: 640px){
    .forum-ad-audio-toggle{
      width:36px;
      height:36px;
      right: calc(
        var(--forum-ad-lens-right, 10px) +
        (var(--forum-ad-lens-size, 40px) - 6px) + 8px
      );
    }
    .forum-ad-audio-toggle__visual{
      width:30px;
      height:30px;
    }
    .forum-ad-audio-toggle__slash{
      width:28px;
    }
  }

  @media (prefers-reduced-motion: reduce){
    .forum-ad-audio-toggle.on{
      animation:none !important;
    }
  }
  .forum-ad-discoveryPrompt {
    position: absolute;
    right: calc(var(--forum-ad-lens-right, 10px) + var(--forum-ad-lens-size, 50px) + 10px);
    bottom: var(--forum-ad-lens-bottom, 10px);
    z-index: 8;
    width: min(330px, calc(100% - 126px));
    min-height: 52px;
    display: grid;
    grid-template-columns: 30px minmax(0, 1fr) 18px;
    align-items: center;
    gap: 9px;
    padding: 9px 10px;
    border: 1px solid rgba(255, 220, 116, 0.03);
    border-radius: 16px;
    color: #f8fbff;
    text-decoration: none;
    background:
      linear-gradient(118deg, rgba(5, 23, 42, .96), rgba(7, 54, 72, .9) 58%, rgba(68, 42, 9, .92)),
      radial-gradient(120% 160% at 10% 10%, rgba(111, 231, 255, .24), transparent 62%);
    box-shadow:
      inset 0 0 24px rgba(111, 231, 255, .1),
      0 12px 30px rgba(0, 0, 0, .48),
      0 0 18px rgba(255, 211, 87, .12);
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    overflow: hidden;
    opacity: 0;
    pointer-events: none;
    transform: translate3d(16px, 6px, 0) scale(.94);
    clip-path: inset(0 0 0 100% round 16px);
    transition:
      opacity .38s ease,
      transform .48s cubic-bezier(.2,.8,.2,1),
      clip-path .52s cubic-bezier(.2,.8,.2,1),
      filter .25s ease;
  }
  .forum-ad-discoveryPrompt::before {
    content: '';
    position: absolute;
    inset: 0;
    transform: translateX(-130%) skewX(-20deg);
    width: 48%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,.2), transparent);
    pointer-events: none;
  }
  .forum-ad-discoveryPrompt[data-visible="1"] {
    opacity: 1;
    pointer-events: auto;
    transform: translate3d(0, 0, 0) scale(1);
    clip-path: inset(0 0 0 0 round 16px);
  }
  .forum-ad-discoveryPrompt[data-visible="1"]::before {
    animation: forumAdPromptShine 2100ms 350ms ease-in-out infinite;
  }
  .forum-ad-discoveryPrompt:hover {
    filter: brightness(1.08) saturate(1.08);
    transform: translate3d(-2px, -1px, 0) scale(1.01);
  }
  .forum-ad-discoveryPrompt:focus-visible {
    outline: 2px solid rgba(255, 226, 111, .96);
    outline-offset: 2px;
  }
  .forum-ad-discoveryIcon {
    width: 30px;
    height: 30px;
    border-radius: 10px;
    display: grid;
    place-items: center;
    color: #06131f;
    font-size: 16px;
    font-weight: 1000;
    background: linear-gradient(135deg, #72ecff, #fff29a 62%, #ffca4e);
    box-shadow: 0 0 16px rgba(111, 231, 255, .38);
  }
  .forum-ad-discoveryCopy {
    min-width: 0;
    display: grid;
    gap: 2px;
    text-align: left;
  }
  .forum-ad-discoveryCopy strong {
    font-size: clamp(11px, 2.3vw, 13px);
    line-height: 1.12;
    letter-spacing: .01em;
    color: #fff1a5;
    text-shadow: 0 0 10px rgba(255, 215, 85, .2);
  }
  .forum-ad-discoveryCopy small {
    font-size: clamp(9px, 2vw, 11px);
    line-height: 1.2;
    color: rgba(230, 246, 255, .9);
    overflow-wrap: anywhere;
  }
  .forum-ad-discoveryArrow {
    color: #7deeff;
    font-size: 18px;
    line-height: 1;
    animation: forumAdPromptArrow 1100ms ease-in-out infinite;
  }

  .forum-ad-lensSvg {
    width: 100%;
    height: 100%;
    display: block;
    overflow: visible;
  }

  .forum-ad-lensOrbit {
    fill: none;
    stroke: var(--forum-ad-lens-outline, rgba(255, 215, 120, .96));
    stroke-width: var(--forum-ad-lens-orbit-w, 1px);
    stroke-linecap: round;
    stroke-dasharray: 2.2 4.4;
    opacity: .92;
    transform-origin: 16px 16px;
    animation: forumAdLensSweep var(--forum-ad-lens-sweep-dur, 2400ms) linear infinite;
  }

  .forum-ad-lensRingOutline {
    fill: none;
    stroke: var(--forum-ad-lens-outline, rgba(255, 215, 120, .96));
    stroke-width: var(--forum-ad-lens-outline-w, 1px);
    opacity: .98;
  }

  .forum-ad-lensRing {
    fill: none;
    stroke: var(--forum-ad-lens-stroke, #6fe7ff);
    stroke-width: var(--forum-ad-lens-ring-w, 1.8px);
    stroke-linecap: round;
    stroke-linejoin: round;
    opacity: .98;
    animation: forumAdLensPulse var(--forum-ad-lens-pulse-dur, 1800ms) ease-in-out infinite;
  }

  .forum-ad-lensHandleOutline {
    fill: none;
    stroke: var(--forum-ad-lens-outline, rgba(255, 215, 120, .96));
    stroke-width: var(--forum-ad-lens-outline-w, 1px);
    stroke-linecap: round;
    stroke-linejoin: round;
    opacity: .98;
  }

  .forum-ad-lensHandle {
    fill: none;
    stroke: var(--forum-ad-lens-handle, #b77dff);
    stroke-width: var(--forum-ad-lens-handle-w, 2.2px);
    stroke-linecap: round;
    stroke-linejoin: round;
    opacity: .98;
    animation: forumAdLensPulse var(--forum-ad-lens-pulse-dur, 1800ms) ease-in-out infinite;
  }

  .forum-ad-lensCore {
    fill: rgba(255,255,255,.16);
    stroke: var(--forum-ad-lens-outline, rgba(255, 215, 120, .96));
    stroke-width: .8px;
    opacity: .92;
  }

  .forum-ad-lensSpark {
    fill: rgba(255,255,255,.95);
    filter: drop-shadow(0 0 6px rgba(255,255,255,.45));
    transform-origin: 24px 9px;
    animation: forumAdLensSparkle var(--forum-ad-lens-sparkle-dur, 1600ms) ease-in-out infinite;
  }

  @keyframes forumAdLensFloat {
    0%, 100% {
      transform: translate3d(0, 0, 0) scale(1);
    }
    50% {
      transform: translate3d(0, -1.5px, 0) scale(1.03);
    }
  }

  @keyframes forumAdLensSweep {
    0% {
      stroke-dashoffset: 0;
      transform: rotate(0deg);
    }
    100% {
      stroke-dashoffset: -13.2;
      transform: rotate(360deg);
    }
  }

  @keyframes forumAdLensPulse {
    0%, 100% {
      filter: brightness(1);
      opacity: .92;
    }
    50% {
      filter: brightness(1.18);
      opacity: 1;
    }
  }

  @keyframes forumAdLensSparkle {
    0%, 100% {
      opacity: .35;
      transform: scale(.8);
    }
    50% {
      opacity: 1;
      transform: scale(1.18);
    }
  }

  @keyframes forumAdLensAura {
    0% { filter: drop-shadow(0 0 7px rgba(111,231,255,.34)) drop-shadow(0 0 14px rgba(183,125,255,.18)); }
    50% { filter: drop-shadow(0 0 13px rgba(111,231,255,.62)) drop-shadow(0 0 24px rgba(255,211,87,.32)); }
    100% { filter: drop-shadow(0 0 7px rgba(111,231,255,.34)) drop-shadow(0 0 14px rgba(183,125,255,.18)); }
  }
  @keyframes forumAdLensRadar {
    0% { opacity: .9; transform: scale(.72); }
    80%, 100% { opacity: 0; transform: scale(1.42); }
  }
  @keyframes forumAdLensCoreGlow {
    0%, 100% { opacity: .55; transform: scale(.82); }
    50% { opacity: 1; transform: scale(1.16); }
  }
  @keyframes forumAdPromptShine {
    0%, 22% { transform: translateX(-130%) skewX(-20deg); }
    58%, 100% { transform: translateX(340%) skewX(-20deg); }
  }
  @keyframes forumAdPromptArrow {
    0%, 100% { transform: translateX(0); opacity: .66; }
    50% { transform: translateX(3px); opacity: 1; }
  }

  @media (max-width: 640px) {
    .forum-ad-lens {
      width: calc(var(--forum-ad-lens-size, 50px) - 4px);
      height: calc(var(--forum-ad-lens-size, 50px) - 4px);
    }
    .forum-ad-discoveryPrompt {
      right: calc(var(--forum-ad-lens-right, 10px) + var(--forum-ad-lens-size, 50px) + 6px);
      width: min(300px, calc(100% - 118px));
      min-height: 48px;
      grid-template-columns: 26px minmax(0, 1fr) 14px;
      gap: 7px;
      padding: 8px;
      border-radius: 14px;
    }
    .forum-ad-discoveryIcon {
      width: 26px;
      height: 26px;
      border-radius: 8px;
      font-size: 14px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .forum-ad-lens,
    .forum-ad-lensOrbit,
    .forum-ad-lensRing,
    .forum-ad-lensHandle,
    .forum-ad-lensSpark,
    .forum-ad-discoveryPrompt::before,
    .forum-ad-discoveryArrow {
      animation: none !important;
    }
  }
`}</style>


      <div
        aria-label={`${label} • ${host}`}
        className="forum-ad-link no-underline"
      >
        <div className="forum-ad-card-body">
          {/* header: только бейдж + домен, без url-строки */}
          <div
            className="forum-ad-header flex items-center gap-2 text-[9px] uppercase tracking-wide text-[color:var(--muted-fore,#9ca3af)]"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'nowrap',
              width: '100%',
            }}
          >
            <div className="flex items-center gap-2">
            <span
                className="qcoinLabel ql7QCoinGoldLabel"
                style={{ fontSize: `${AD_LABEL_FONT_SIZE_PX}px` }}
             >
                {label}
              </span>
              <span className="truncate max-w-[140px] font-medium">
                {/* домен можно вернуть сюда, если захочешь */}
              </span>
            </div>
            <button
              type="button"
              data-forum-open-ads-page="1"
              data-vf-programmatic-toggle="1"
              data-vf-gesture-exempt="1"
              onClick={handleOpenAdsPage}
              className="btn"
              style={{
                fontSize: '12px',
                padding: '6px 12px',
                borderRadius: 999,
                marginLeft: 'auto',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {typeof t === 'function'
                ? t('forum_ad_place', 'Разместить')
                : 'Разместить'}
            </button>
          </div>

          {/* media: заполняет карточку */}
<div
  className="relative mt-0.5 border border-[color:var(--border,#27272a)] forum-ad-media-slot"
  data-layout={isFluid ? 'fluid' : 'fixed'}
  data-ad-surface-activate={canActivateMedia ? '1' : undefined}
  role={canActivateMedia ? 'button' : undefined}
  tabIndex={canActivateMedia ? 0 : undefined}
  aria-label={canActivateMedia ? mediaActivationLabel : undefined}
  onClick={activateMediaSurface}
  onKeyDown={handleMediaSurfaceKeyDown}
  {...(mediaSlotAttrs || {})}
>

 {media.kind === 'skeleton' && (
              <div className="animate-pulse w-full h-full bg-[color:var(--skeleton,#111827)]" />
            )}

{media.kind === 'video' && media.src && liveModerationStatus === 'pending' && (
  <span className="forum-video-moderation-badge" aria-label="moderation">moderation</span>
)}

{media.kind === 'video' && media.src && (
  <div className="forum-ad-media-fill">
{media.poster ? (
  // eslint-disable-next-line @next/next/no-img-element -- persisted ad poster reused as a decorative ambient layer.
  <img
    src={media.poster}
    alt=""
    aria-hidden="true"
    draggable={false}
    loading="lazy"
    decoding="async"
    referrerPolicy="no-referrer"
    className="forum-ad-ambient-backdrop"
    data-ambient-media-backdrop="ad-video"
  />
) : null}
{nativeAdVideoMountAllowed ? <video
  ref={mergeRefs(localVideoRef, videoRef)}
  className={['forum-ad-fit', videoClassName].filter(Boolean).join(' ')}
  src={deferNativeSrc ? undefined : media.src}
  poster={media.poster || undefined}
  data-src={media.src}
  data-ad-media-src={media.src}
  data-ad-media-kind="video"
  muted={isAdMuted}
  playsInline
  referrerPolicy="no-referrer"
  preload={AD_NATIVE_VIDEO_PRELOAD_IDLE}
  draggable={false}
  {...restVideoAttrs}
/> : null}
  </div>
)}

            {media.kind === 'youtube' && media.src && (
<div
  className="relative overflow-hidden rounded-lg"
  style={isFluid ? { width: '100%', aspectRatio: '16 / 9' } : { width: '100%', height: '100%' }}
>
  <iframe
    ref={mergeRefs(localIframeRef, iframeRef)}
    className={youtubeClassName || undefined}
    src={deferExternalSrc ? undefined : youtubeSrc}
    data-src={youtubeSrc}
    data-ad-media-src={media.src}
    data-ad-media-kind="youtube"
    title="YouTube video"
    frameBorder="0"
    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
    allowFullScreen
    style={{
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      borderRadius: 10,
      pointerEvents: 'none',
    }}
    {...restYoutubeAttrs}
  />
  {externalPosterVisible && (
    <div
      data-forum-external-poster="1"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        background: '#000',
        pointerEvents: 'auto',
      }}
    >
      {externalPosterSrc ? (
        <img
          src={externalPosterSrc}
          alt=""
          draggable={false}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            objectPosition: 'center',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />
      ) : null}
      <button
        type="button"
        className="forum-ad-external-play-button"
        data-forum-external-poster-play-button="1"
        aria-label={mediaActivationLabel}
        onClick={activateMediaSurface}
      >
        <img
          src="/youtube/play.webp"
          alt=""
          draggable={false}
          data-forum-external-poster-play="1"
        />
      </button>
    </div>
  )}
</div>

            )}

            {media.kind === 'tiktok' && media.src && tiktokActive && (
<div
  className="relative overflow-hidden rounded-lg"
  style={isFluid ? { width: '100%', aspectRatio: '9 / 16' } : { width: '100%', height: '100%' }}
>
  <iframe
    ref={mergeRefs(localIframeRef, iframeRef)}
    className={tiktokClassName || undefined}
    src={deferExternalSrc ? undefined : tiktokSrc}
    data-src={tiktokSrc}
    data-ad-media-src={media.src}
    data-ad-media-kind="tiktok"
    title="TikTok video"
    frameBorder="0"
    allow="encrypted-media; fullscreen; picture-in-picture"
    style={{
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      borderRadius: 10,
      pointerEvents: 'none',
    }}
    {...restTiktokAttrs}
  />
  {externalPosterVisible && (
    <div
      data-forum-external-poster="1"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        background: '#000',
        pointerEvents: 'auto',
      }}
    >
      {externalPosterSrc ? (
        <img
          src={externalPosterSrc}
          alt=""
          draggable={false}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            objectPosition: 'center',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />
      ) : null}
      <button
        type="button"
        className="forum-ad-external-play-button"
        data-forum-external-poster-play-button="1"
        aria-label={mediaActivationLabel}
        onClick={activateMediaSurface}
      >
        <img
          src="/youtube/play.webp"
          alt=""
          draggable={false}
          data-forum-external-poster-play="1"
        />
      </button>
    </div>
  )}
</div>

            )}

            {media.kind === 'tiktok' && media.src && !tiktokActive && (
              <div className="w-full h-full flex items-center justify-center text-[11px] text-[color:var(--muted-fore,#9ca3af)]">
                {host}
              </div>
            )}


{media.kind === 'image' && media.src && (
  <div className="forum-ad-media-fill">
    {/* eslint-disable-next-line @next/next/no-img-element -- original ad creative reused as a decorative ambient layer. */}
    <img
      src={media.src}
      alt=""
      aria-hidden="true"
      draggable={false}
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      className="forum-ad-ambient-backdrop"
      data-ambient-media-backdrop="ad-image"
    />
    {isFluid ? (
      <img
        src={media.src}
        alt={host}
        className="forum-ad-fit"
        draggable={false}
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
      />
    ) : (
      <NextImage
        src={media.src}
        alt={host}
        fill
        className="forum-ad-fit transition-opacity duration-200"
        style={{ objectFit: 'contain', objectPosition: 'center' }}
        draggable={false}
        unoptimized
        referrerPolicy="no-referrer"
      />
    )}
  </div>
)}

            {media.kind === 'placeholder' && (
              <div className="w-full h-full flex items-center justify-center text-[11px] text-[color:var(--muted-fore,#9ca3af)]">
                {host}
              </div>
            )}

{showSoundButton && (
  <button
    type="button"
    onClick={handleToggleSound}
    className={`forum-ad-audio-toggle ${isAdMuted ? 'off' : 'on'}`}
    aria-label={isAdMuted ? 'muted' : 'unmuted'}
    aria-pressed={!isAdMuted}
    data-ad-sound-toggle="1"
  >
    <span className="forum-ad-audio-toggle__visual" aria-hidden="true">
      <ViewportAnimatedImage
        className="forum-ad-audio-toggle__gif"
        animatedSrc="/audio/bgaudio.gif"
        alt=""
        width={34}
        height={34}
        unoptimized
        draggable={false}
      />
      <span className="forum-ad-audio-toggle__slash" />
    </span>
  </button>
)}

            {media.kind === 'video' && clickHref && (
              <a
                href={clickHref}
                target="_blank"
                rel="noopener noreferrer nofollow ugc"
                className="forum-ad-discoveryPrompt"
                data-visible={discoveryVisible ? '1' : '0'}
                data-ad-destination-trigger="1"
                aria-hidden={discoveryVisible ? undefined : 'true'}
                tabIndex={discoveryVisible ? 0 : -1}
                onClick={handleDestinationClick}
              >
                <span className="forum-ad-discoveryIcon" aria-hidden="true">✦</span>
                <span className="forum-ad-discoveryCopy">
                  <strong>{discoveryTitle}</strong>
                  <small>{discoveryBody}</small>
                </span>
                <span className="forum-ad-discoveryArrow" aria-hidden="true">›</span>
              </a>
            )}

            <a
              href={clickHref}
              target="_blank"
              rel="noopener noreferrer nofollow ugc"
              className="forum-ad-lens"
              data-ad-destination-trigger="1"
              aria-label={destinationLabel}
              onClick={handleDestinationClick}
            >
              <svg
                className="forum-ad-lensSvg"
                viewBox="0 0 40 40"
                focusable="false"
                aria-hidden="true"
              >
                <circle
                  className="forum-ad-lensOrbit"
                  cx="16"
                  cy="16"
                  r="10.75"
                />
                <circle
                  className="forum-ad-lensRingOutline"
                  cx="16"
                  cy="16"
                  r="7.7"
                />
                <circle
                  className="forum-ad-lensRing"
                  cx="16"
                  cy="16"
                  r="7.1"
                />
                <path
                  className="forum-ad-lensHandleOutline"
                  d="M21.15 21.15 L30.15 30.15"
                />
                <path
                  className="forum-ad-lensHandle"
                  d="M21.15 21.15 L30.15 30.15"
                />
                <circle
                  className="forum-ad-lensCore"
                  cx="16"
                  cy="16"
                  r="1.55"
                />
                <circle
                  className="forum-ad-lensSpark"
                  cx="24.2"
                  cy="8.9"
                  r="1.05"
                />
              </svg>
            </a>

            <div className="pointer-events-none абсолют inset-0 rounded-lg border border-transparent qshine" />
          </div>
        </div>
      </div>
    </div>
  );
}
